import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  IconButton,
  InputBase,
  Link as MuiLink,
  Paper,
  Snackbar,
  Stack,
  SvgIcon,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {
  supportAgentApi,
  ChatListItem,
  ChatDoc,
  ChatStatus,
  ChatTurn,
  ChatAttachment,
  ChatFeedback,
  CHAT_FEEDBACK_RESOLUTION_LABEL,
  chatReviewReasonText,
  signedInEmail,
} from './support-agent-api';
import { ChatLabelPanel } from './chat-label-panel';
import { RatingsStack } from './rating-stars';
import { ChannelIcon, CustomerSummary, HandlerIcon, PastChats, loadPersonChats } from './customer-history';
import { AttachmentList, AttachmentPickerButton, PendingAttachmentList, usePendingAttachments } from './chat-attachments';
import { useLiveChatEvent, useLiveChatSocket } from './live-chat-socket';
import {
  CustomerMatch,
  WhoseTurn,
  chatOutcome,
  chatRatings,
  closedByText,
  compareActive,
  customerLabel,
  docFromListItem,
  escalationReasonLabel,
  firstReplyMinutes,
  formatDuration,
  groupByCustomer,
  handledBy,
  initialsFor,
  isTestChat,
  isUrgent,
  lastMessagePreview,
  listItemFromDoc,
  loginChannels,
  notOpenedYet,
  unseenCount,
  assistantReplying,
  quietSince,
  reviewKindFor,
  shortChatDate,
  waitingSince,
  whoseTurn,
} from './chat-queue';

// Same 5s cadence the customer widget polls its own chat at; the POM Master
// inbox refreshes on its own, slower cadence (Carlos, 9/9) since nobody is
// staring at a "Thinking…" indicator here.
const CHATS_POLL_INTERVAL_MS = 15_000;
// With the live socket connected (live-chat-socket.ts) the server's
// chat_update doorbells drive the list and the open drawer; the poll is only
// a slow safety net then.
export const CHATS_POLL_FALLBACK_INTERVAL_MS = 60_000;
// A doorbell per write would reload the list once per turn of a busy chat —
// coalesce a burst into one reload.
export const CHATS_LIVE_REFRESH_DEBOUNCE_MS = 300;
// "Derek is typing" (POST /chat/:id/typing): the first keystroke sends it,
// then at most one every few seconds while the reply box has text.
export const CHATS_TYPING_SEND_INTERVAL_MS = 3000;
// "Seen by support" (POST /chat/:id/staff-seen): sent while a person has a
// chat open in the drawer, at most this often per chat.
export const CHATS_STAFF_SEEN_MIN_INTERVAL_MS = 2000;
// "Customer is typing" (chat_customer_typing): how long one note keeps the
// dots up with no renewal, like the widget's staff dots.
export const CHATS_CUSTOMER_TYPING_TTL_MS = 6000;
// A stop holds the dots this long (Michael, 10/23: the widget stops on Send,
// and dropping them at once showed the old preview — the assistant's — for a
// moment before the customer's message landed). Their message clears sooner.
export const CHATS_CUSTOMER_TYPING_STOP_GRACE_MS = 1500;
// Another rep's typing (chat_staff_typing) lasts as long as the customer's.
export const CHATS_STAFF_TYPING_TTL_MS = CHATS_CUSTOMER_TYPING_TTL_MS;
// The socket takes a moment to connect when the inbox opens: say nothing
// about it for this long (Michael, 10/23: "Reconnecting…" flashed on every
// open, though nothing had been lost). A drop after it was live shows at once.
export const CHATS_LIVE_CONNECT_GRACE_MS = 5000;
// The split view runs to the bottom of the screen (Michael, 10/23: no box
// floating in empty page), this far from it, and never shorter than the min.
export const CHATS_SPLIT_BOTTOM_GAP = 16;
export const CHATS_SPLIT_MIN_HEIGHT = 420;
// The app's AI-chat button is fixed over the bottom-right corner (56px, 20px
// from the edges): whatever sits at the bottom of the rightmost column keeps
// this much room so Send, Save and Resolve are never under it.
const CHAT_BUTTON_CLEARANCE = '64px';
// Find a customer: wait this long after the last keystroke, then search
// once the text has CHATS_SEARCH_MIN_CHARS characters (pom-2-api's minimum).
export const CHATS_SEARCH_DEBOUNCE_MS = 300;
export const CHATS_SEARCH_MIN_CHARS = 2;
// One search returns this many chats, grouped into people.
export const CHATS_SEARCH_LIMIT = 100;
// The longest search pom-2-api accepts.
export const CHATS_SEARCH_MAX_CHARS = 100;
export const CUSTOMER_TYPING_LABEL = 'Customer is typing';
// The Active list: every open chat (escalated and still-with-the-assistant),
// each fetched in full once so the list can say whose turn it is. Both are
// short lists — escalated chats only leave by being resolved, AI chats close
// after the auto-resolve window (12 hours by default) — so one page of each
// covers them.
export const CHATS_ACTIVE_LIMIT = 200;
// Past this many pages of one open status the list says how many it left out.
export const CHATS_ACTIVE_MAX_PAGES = 5;
// Needs review / Closed load this many at a time, "Show more" adds another.
export const CHATS_VIEW_PAGE_SIZE = 50;
// Support's-turn waits past this read as late (amber).
export const CHATS_REPLY_TARGET_MS = 10 * 60_000;
// How often the list's relative times ("Waiting 12m") move on: every second
// while a row counts seconds (Michael, 10/23: "Waiting 12s" sat still).
const CHATS_CLOCK_TICK_MS = 30_000;
export const CHATS_CLOCK_FAST_TICK_MS = 1000;

// The inbox's Hide test chats switch, remembered per browser. Storage can
// be blocked (private windows): then it starts off and isn't remembered.
export const HIDE_TEST_CHATS_STORAGE_KEY = 'supportAgent.chats.hideTestChats';
function readHideTestChats(): boolean {
  try {
    return window.localStorage.getItem(HIDE_TEST_CHATS_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}
function writeHideTestChats(on: boolean) {
  try {
    window.localStorage.setItem(HIDE_TEST_CHATS_STORAGE_KEY, String(on));
  } catch {
    // Not remembered; the switch still works for this visit.
  }
}

/** `map` without `key`, the same object when it isn't there. */
function withoutKey<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

const LIST_ERROR_COPY = 'Could not load chats — try again';
const DETAIL_ERROR_COPY = 'Could not load this conversation — try again';
const REPLY_ERROR_COPY = "Couldn't send that reply — try again";
const RESOLVE_ERROR_COPY = "Couldn't resolve this chat — try again";
const ARCHIVE_ERROR_COPY = "Couldn't retry the archive — try again";

// Surfaces ChatDoc.resolvedBy in the drawer (Interrogator finding, 2026-09-11
// round 2 — the field was already on every resolve response but had no
// consumer, so staff couldn't tell a customer self-resolve from a staff
// resolve or the idle-chat sweep). 'ai' = the customer agreed to
// the assistant's offer to close (pom-2-api, Michael 9/24). A staff resolve
// names the rep once pom-2-api #604 stores it (resolvedByLabel below).
const RESOLVED_BY_LABEL: Record<'customer' | 'staff' | 'inactivity' | 'ai', string> = {
  customer: 'by the customer',
  staff: 'by staff',
  inactivity: 'automatically (inactive)',
  ai: 'by the AI assistant (customer agreed)',
};

function resolvedByLabel(chat: Pick<ChatDoc, 'resolvedBy' | 'resolvedByStaff'>, myEmail?: string): string {
  if (!chat.resolvedBy) return '';
  if (chat.resolvedBy === 'staff' && chat.resolvedByStaff) {
    return `by ${myEmail && chat.resolvedByStaff === myEmail ? 'you' : chat.resolvedByStaff}`;
  }
  return RESOLVED_BY_LABEL[chat.resolvedBy];
}

const zendeskTicketUrl = (id: string) => `https://pomsafe.zendesk.com/agent/tickets/${id}`;

// The list's views (split view, Michael 10/22). 'active' is every open
// chat, ordered by whose turn it is. 'closed' is resolved chats, newest
// first. 'label' is Closed with its Needs label filter on (Michael, 10/23:
// a filter, not its own tab; pom-2-api #604's `needsLabel`): closed chats
// that were escalated or rated "Not at all", until support labels them.
type ChatsView = 'active' | 'label' | 'closed';

/** "Feedback: Partially", then each rating as stars on its own line (Michael, 10/23). */
function FeedbackSummary({ feedback }: { feedback: ChatFeedback }) {
  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Feedback: {CHAT_FEEDBACK_RESOLUTION_LABEL[feedback.resolution]}
      </Typography>
      {/* Support's row always, 0 when no person joined (Michael, 10/23). */}
      <RatingsStack assistant={feedback.assistantRating} support={feedback.supportRating ?? 0} />
    </>
  );
}

/** Where the open chat came from, beside the name: a phone (the POM app) or a computer (the website). */
function AppChip({ channel }: { channel?: ChatListItem['channel'] }) {
  return <ChannelIcon channel={channel === 'app' ? 'app' : 'web'} size={18} testId="chat-app-chip" />;
}

function StatusChip({ status }: { status: ChatStatus }) {
  if (status === 'escalated') return <Chip size="small" color="warning" label="Escalated" />;
  // The design's light "closed" pill: white on success.main is under AA.
  if (status === 'resolved') return <Chip size="small" label="Resolved" sx={{ ...WHOSE_TURN_SX.closed, fontWeight: 600 }} />;
  return <Chip size="small" variant="outlined" label="AI" />;
}

/** `4:15 PM`, or `9/9/2026 4:15 PM` when `showDate` — see `showDateFor` below. */
function formatTurnTimestamp(at: string, showDate: boolean): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return showDate ? `${d.toLocaleDateString()} ${time}` : time;
}

/**
 * "Seen 4:15 PM" for under our last reply (staff or the AI) once the
 * customer's `lastSeenAt` (pom-2-api POST /chat/:id/seen, from the web
 * widget or the app) reaches it — Michael, 10/13. Only while that reply is
 * the newest turn: a customer turn after it already shows they read it.
 * The date is added when the stamp fell on a different day than the reply.
 */
export function seenLabelFor(chat: Pick<ChatDoc, 'turns' | 'lastSeenAt'>): string | null {
  const last = chat.turns[chat.turns.length - 1];
  if (!last || last.role === 'customer' || !chat.lastSeenAt) return null;
  const seenAt = new Date(chat.lastSeenAt);
  const at = new Date(last.at);
  if (Number.isNaN(seenAt.getTime()) || Number.isNaN(at.getTime()) || seenAt.getTime() < at.getTime()) return null;
  return `Seen ${formatTurnTimestamp(chat.lastSeenAt, seenAt.toDateString() !== at.toDateString())}`;
}

/**
 * A background `fetchDetail` refetch replaces `detail` wholesale — if a live
 * `chat_seen` event already moved `lastSeenAt` forward (same chat) while
 * that fetch was in flight, the fetch's own (now-stale) value must not undo
 * it, or "Seen" disappears until the next poll (Michael, 10/13 review).
 * Keeps the later of the two stamps; an invalid/missing one loses.
 */
export function laterLastSeenAt(current: string | undefined, fetched: string | undefined): string | undefined {
  if (!current) return fetched;
  if (!fetched) return current;
  const c = new Date(current).getTime();
  const f = new Date(fetched).getTime();
  if (Number.isNaN(c)) return fetched;
  if (Number.isNaN(f)) return current;
  return f >= c ? fetched : current;
}

/** Show the date on a turn only when it's the first turn or the calendar day changed since the previous one. */
function showDateFor(turns: ChatTurn[], index: number): boolean {
  if (index === 0) return true;
  return new Date(turns[index - 1].at).toDateString() !== new Date(turns[index].at).toDateString();
}

/** Three pulsing dots — the typing cue, in the transcript and on list rows. */
function TypingDots({ color = 'text.secondary', size = 8 }: { color?: string; size?: number }) {
  return (
    <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          component="span"
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            bgcolor: color,
            display: 'block',
            animation: 'chats-typing-dot 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes chats-typing-dot': { '0%, 80%, 100%': { opacity: 0.3 }, '40%': { opacity: 1 } },
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        />
      ))}
    </Box>
  );
}

/** Three pulsing dots on the customer's side: "Customer is typing". */
function CustomerTypingDots() {
  return (
    <Box
      data-testid="chat-customer-typing"
      aria-label={CUSTOMER_TYPING_LABEL}
      role="status"
      sx={{ display: 'flex', justifyContent: 'flex-start' }}
    >
      <Box sx={{ px: 1.75, py: 1.5, bgcolor: BUBBLE.customer.bg, borderRadius: BUBBLE.customer.radius, display: 'flex' }}>
        <TypingDots />
      </Box>
    </Box>
  );
}

/** Three pulsing dots on the assistant's side while its reply is on the way (Michael, 10/26). */
function AssistantTypingDots() {
  return (
    <Box
      data-testid="chat-assistant-typing"
      aria-label="Assistant is typing"
      role="status"
      sx={{ display: 'flex', justifyContent: 'flex-end' }}
    >
      <Box sx={{ px: 1.75, py: 1.5, bgcolor: BUBBLE.agent.bg, borderRadius: BUBBLE.agent.radius, display: 'flex' }}>
        <TypingDots color="#0B5A86" />
      </Box>
    </Box>
  );
}

// The transcript's bubbles (split view design, Michael 10/22): the customer
// on the left in grey, the assistant on the right in light blue, support on
// the right in solid dark blue (white text at 5.3:1).
const BUBBLE = {
  customer: { bg: '#F4F6F8', color: 'text.primary', radius: '4px 16px 16px 16px' },
  agent: { bg: '#E6F6FC', color: 'text.primary', radius: '16px 4px 16px 16px' },
  human: { bg: 'primary.dark', color: 'common.white', radius: '16px 4px 16px 16px' },
} as const;

/**
 * One turn in the transcript — same display rule as the widget: derive text
 * from the flags, not `body` alone. Laid out from the support side, like any
 * chat app (Michael, 10/10): our side (staff and the AI) on the right, the
 * customer on the left. The name and time sit above the bubble; `sources`
 * (the articles behind an assistant reply) sit under it.
 */
type RefreshAttachment = (attachment: ChatAttachment) => Promise<ChatAttachment | undefined>;

// Chat attachments (Michael, 10/14): a customer or staff turn may carry
// files; a files-only turn shows the files under the label, no empty line.
function TurnLine({
  turn,
  showDate,
  onRefreshAttachment,
  sources,
}: {
  turn: ChatTurn;
  showDate: boolean;
  onRefreshAttachment?: RefreshAttachment;
  sources?: React.ReactNode;
}) {
  const label = turn.role === 'customer' ? 'Customer' : turn.role === 'human' ? turn.by ?? 'Support' : 'Assistant';
  let body = turn.body;
  if (turn.role === 'agent') {
    if (turn.urgent) body = `[Flagged urgent${turn.urgencyReason ? `: ${turn.urgencyReason}` : ''}]`;
    else if (turn.skipped) body = '[Skipped — sales/vendor or spam]';
    else if (!turn.body) body = '[No draft produced]';
  }
  const attachments = turn.attachments ?? [];
  const ours = turn.role !== 'customer';
  const bubble = BUBBLE[turn.role];
  const bubbleSx = {
    px: 1.75,
    py: 1.25,
    borderRadius: bubble.radius,
    bgcolor: bubble.bg,
    color: bubble.color,
    lineHeight: 1.55,
    // A long email or link wraps instead of pushing the bubble past the
    // transcript's edge (attack/defend round 3).
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  };
  return (
    <Box data-testid={`chat-turn-${turn.role}`} sx={{ display: 'flex', justifyContent: ours ? 'flex-end' : 'flex-start' }}>
      <Box sx={{ maxWidth: '80%', display: 'flex', flexDirection: 'column', alignItems: ours ? 'flex-end' : 'flex-start', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label} · {formatTurnTimestamp(turn.at, showDate)}
        </Typography>
        {attachments.length === 0 ? (
          <Typography variant="body2" sx={{ ...bubbleSx, whiteSpace: 'pre-wrap' }}>
            {body}
          </Typography>
        ) : (
          <Box sx={bubbleSx}>
            {body && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mb: 1 }}>
                {body}
              </Typography>
            )}
            <AttachmentList attachments={attachments} refresh={onRefreshAttachment} />
          </Box>
        )}
        {sources}
      </Box>
    </Box>
  );
}

/** Where the chat reached a person, drawn across the transcript at that point. */
function EscalationDivider({ chat }: { chat: ChatDoc }) {
  if (!chat.escalation?.at) return null;
  const parts = [escalationReasonLabel(chat.escalation.reason), formatTurnTimestamp(chat.escalation.at, false)];
  if (chat.zendeskTicketId) parts.push(`Zendesk #${chat.zendeskTicketId}`);
  return (
    <Box
      data-testid="chat-escalation-divider"
      sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: 'text.secondary' }}
    >
      <Box sx={{ flex: '1 1 auto', height: '1px', bgcolor: 'divider' }} />
      <Typography variant="caption" sx={{ textAlign: 'center' }}>
        {parts.join(' · ')}
      </Typography>
      <Box sx={{ flex: '1 1 auto', height: '1px', bgcolor: 'divider' }} />
    </Box>
  );
}

/** Index of the first turn at or after the escalation — where its divider goes. */
function escalationIndex(chat: ChatDoc): number {
  if (!chat.escalation?.at) return -1;
  const at = new Date(chat.escalation.at).getTime();
  const i = chat.turns.findIndex((t) => new Date(t.at).getTime() >= at);
  return i === -1 ? chat.turns.length : i;
}

// pom-2-api's POST /support-agent/kb/titles limit (ids: max 50).
const KB_TITLES_BATCH = 50;

const kbArticleHref = (id: string) => `/support-agent?tab=kb&kbDoc=${encodeURIComponent(id)}`;

/**
 * The articles behind one assistant reply (split view design, Michael
 * 10/22): the ones it cited, then the others it was given. Cited ids are the
 * model's own picks from what it was given; the rest come from the same
 * stored turn (pom-2-api keeps citedDocIds and suppliedDocIds on every
 * assistant turn). Each opens in the Knowledge Base tab, in a new tab.
 */
/**
 * What the assistant was given for the reply before this one (the last
 * assistant turn that records it), to show what changed (Michael, 10/23).
 */
function previousGiven(turns: ChatTurn[], index: number): string[] | undefined {
  for (let i = index - 1; i >= 0; i -= 1) {
    const turn = turns[i];
    if (turn.role === 'agent' && turn.suppliedDocIds !== undefined) return turn.suppliedDocIds;
  }
  return undefined;
}

/**
 * What this reply searched the knowledge base for (Michael, 10/28): the
 * query its own search ran, or that it ran none, and who decided — so a
 * reply built on the wrong articles shows whether the query or the ranking
 * missed. Nothing for turns that predate the field.
 */
function ReplySearch({ turn }: { turn: ChatTurn }) {
  if (turn.searchQuery === undefined) return null;
  const byPlanner = turn.searchDecidedBy === 'planner';
  if (turn.searchQuery === null) {
    return (
      <Box data-testid="chat-sources-search" sx={{ mb: 1 }}>
        <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary' }}>
          No new search
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {byPlanner
            ? 'The search planner judged the articles already given were enough.'
            : 'A short reply, so the articles already given were used.'}
        </Typography>
      </Box>
    );
  }
  return (
    <Box data-testid="chat-sources-search" sx={{ mb: 1 }}>
      <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary' }}>
        Searched for
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.primary', wordBreak: 'break-word' }}>“{turn.searchQuery}”</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {byPlanner ? 'Query written by the search planner' : 'Query from the fallback word rules'}
      </Typography>
      {turn.searchPickedBy && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} data-testid="chat-sources-pick">
          {turn.searchPickedBy === 'picker'
            ? `The article picker chose ${pickedCount(turn)} of ${turn.searchCandidateDocIds?.length ?? 0} candidates`
            : 'The article picker chose none or did not answer, so the top results were used'}
        </Typography>
      )}
    </Box>
  );
}

/** How many of a reply's candidates became its fresh articles. */
function pickedCount(turn: ChatTurn): number {
  const candidates = turn.searchCandidateDocIds ?? [];
  return (turn.retrievedDocIds ?? []).filter((id) => candidates.includes(id)).length;
}

function ReplySources({ turn, titles, before }: { turn: ChatTurn; titles: Record<string, string>; before?: string[] }) {
  const [open, setOpen] = useState(false);
  const cited = turn.citedDocIds ?? [];
  const given = (turn.suppliedDocIds ?? []).filter((id) => !cited.includes(id));
  // Against the previous reply (Michael, 10/23): what came in, what fell out.
  const now = turn.suppliedDocIds ?? [];
  const added = before && turn.suppliedDocIds ? now.filter((id) => !before.includes(id)) : [];
  const dropped = before && turn.suppliedDocIds ? before.filter((id) => !now.includes(id)) : [];
  // The article picker's rejects (Michael, 10/28): what the wide search found
  // that this reply was not given, so a miss shows whether the search or the
  // pick was at fault.
  const notPicked = (turn.searchCandidateDocIds ?? []).filter((id) => !now.includes(id));
  if (turn.role !== 'agent' || turn.urgent || turn.skipped) return null;
  // A reply given nothing still says what it lost since the last one (attack/defend 10/26).
  if (cited.length === 0 && given.length === 0 && dropped.length === 0) {
    // Only a turn that records what it was given can say nothing was:
    // turns from before suppliedDocIds (9/9-9/11) carry citedDocIds alone.
    if (turn.suppliedDocIds === undefined) return null;
    return (
      <Typography variant="caption" color="text.secondary" data-testid="chat-sources-none">
        No knowledge base article was used
        {typeof turn.searchQuery === 'string' ? ` · searched for “${turn.searchQuery}”` : ''}
      </Typography>
    );
  }
  const summary =
    cited.length === 0 && given.length === 0
      ? `No article used · ${dropped.length} dropped`
      : [`${cited.length} article${cited.length === 1 ? '' : 's'} cited`, ...(given.length ? [`${given.length} more given`] : [])].join(' · ');
  const link = (id: string, strong: boolean) => (
    <MuiLink
      key={id}
      href={kbArticleHref(id)}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ fontSize: 13, fontWeight: strong ? 600 : 400, color: strong ? 'primary.dark' : 'text.secondary', display: 'block', py: 0.25 }}
    >
      {titles[id] ?? `Article ${id.slice(-6)}`} <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
    </MuiLink>
  );
  return (
    <Box sx={{ alignSelf: 'stretch', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="chat-sources-toggle"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: 'primary.dark',
          px: 0.5,
          minHeight: 32,
          borderRadius: 1,
          gap: 0.5,
          fontFamily: 'inherit',
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.dark', outlineOffset: 2 },
        }}
      >
        {summary}
        <ExpandMoreIcon fontSize="small" sx={{ transform: open ? 'rotate(180deg)' : 'none' }} />
      </ButtonBase>
      {open && (
        <Box
          data-testid="chat-sources"
          sx={{ alignSelf: 'stretch', border: 1, borderColor: 'divider', borderRadius: 2.5, px: 1.75, py: 1.5, bgcolor: 'background.paper' }}
        >
          <ReplySearch turn={turn} />
          {cited.length > 0 && (
            <>
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary' }}>
                Cited in this reply
              </Typography>
              {cited.map((id) => link(id, true))}
            </>
          )}
          {given.length > 0 && (
            <>
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary', mt: cited.length ? 1 : 0 }}>
                Also given to the assistant
              </Typography>
              {given.map((id) => link(id, false))}
            </>
          )}
          {added.length > 0 && (
            <Box data-testid="chat-sources-added">
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary', mt: 1 }}>
                New since the last reply · {added.length}
              </Typography>
              {added.map((id) => link(id, false))}
            </Box>
          )}
          {dropped.length > 0 && (
            <Box data-testid="chat-sources-dropped">
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary', mt: 1 }}>
                Dropped since the last reply · {dropped.length}
              </Typography>
              {dropped.map((id) => link(id, false))}
            </Box>
          )}
          {notPicked.length > 0 && (
            <Box data-testid="chat-sources-not-picked">
              <Typography variant="overline" sx={{ display: 'block', lineHeight: 2, fontWeight: 700, color: 'text.secondary', mt: 1 }}>
                Considered, not picked · {notPicked.length}
              </Typography>
              {notPicked.map((id) => link(id, false))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

const WHOSE_TURN_LABEL: Record<WhoseTurn, string> = {
  support: "Support's turn",
  customer: "Customer's turn",
  assistant: 'Assistant',
  closed: 'Closed',
};

// Amber for "someone is waiting on us", grey for "waiting on them", blue for
// the assistant — told apart by lightness as well as hue.
const WHOSE_TURN_SX: Record<WhoseTurn, { bgcolor: string; color: string }> = {
  support: { bgcolor: '#FFF7CD', color: '#7A4F01' },
  customer: { bgcolor: '#F4F6F8', color: '#454F5B' },
  assistant: { bgcolor: '#E6F6FC', color: '#0B5A86' },
  closed: { bgcolor: '#E9FCD4', color: '#08660D' },
};
const URGENT_SX = { bgcolor: '#FFE7D9', color: '#7A0C2E' };

type RowTone = 'plain' | 'late' | 'urgent';
const ROW_TONE_SX: Record<RowTone, { bgcolor: string; color: string }> = {
  plain: { bgcolor: 'transparent', color: 'text.secondary' },
  late: { bgcolor: '#FFF7CD', color: '#7A4F01' },
  urgent: URGENT_SX,
};

interface ChatRowModel {
  chatId: string;
  row: ChatListItem;
  label: string;
  channel?: ChatListItem['channel'];
  chip: string;
  chipSx: { bgcolor: string; color: string };
  // Grey on closed rows (the design): red stays for Urgent only.
  avatarSx: { bgcolor: string; color: string };
  timeText: string;
  tone: RowTone;
  urgent: boolean;
  // A closed chat's length, under its date: "12 turns".
  turnsText?: string;
  // An assistant chat whose reply is on the way (Michael, 10/26).
  assistantTyping?: boolean;
  // How many customer messages no person has seen (0: none).
  unseen: number;
  preview: string;
  // One customer's list: their initials on every row, how each chat went,
  // and where it came from (App or Web — they may chat from both).
  avatarText?: string;
  detail?: string;
  source?: 'app' | 'web';
  // Who handled it: the assistant alone, or support once escalated (Michael, 10/23).
  handler: 'assistant' | 'support';
  // The customer's answer to "Was your issue resolved?", color-coded, right
  // above the stars (Michael, 10/23).
  resolution?: { text: string; sx: { bgcolor: string; color: string } };
  // The customer's ratings, stacked under the row (assistant, then support).
  ratings?: { assistant: number; support?: number };
  // Support's label, by its reason, once saved: a tag on the row (Michael, 10/23).
  labelled?: string;
  // A closed chat that should have a label and has none: a dashed tag.
  missingLabel?: boolean;
}

// Fits "Resolution: Not at all", the longest answer.
const RESOLUTION_PILL_WIDTH = 136;

// A typing note on a row's chip line: small, colored by who it is.
const rowTypingSx = (color: string) => ({ color, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 });

const pillSx = { height: 20, fontSize: 11, fontWeight: 700, borderRadius: 999, '& .MuiChip-label': { px: 1 } };

/**
 * The labelled tag's outline, dashed: a label that should be there and isn't
 * (Michael, 10/23). Drawn on the solid tag's own shape (LocalOffer), inset by
 * half the stroke, so the two tags are the same size.
 */
function MissingLabelIcon() {
  return (
    <SvgIcon viewBox="0 0 24 24" sx={{ fontSize: 15 }}>
      <path
        d="M20.8 12.2l-9-9a1.4 1.4 0 00-1-.4H4a1.2 1.2 0 00-1.2 1.2v6.8c0 .4.1.7.4 1l9 9c.3.3.6.4 1 .4s.7-.1 1-.4l6.6-6.6c.3-.3.4-.6.4-1s-.1-.7-.4-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeDasharray="2.6 2.2"
        strokeLinejoin="round"
      />
      <circle cx="5.5" cy="5.5" r="1.5" fill="currentColor" />
    </SvgIcon>
  );
}

/** One chat in the left-hand list. A button: it selects the chat into the right-hand pane. */
function ChatRow({
  model,
  selected,
  typing,
  staffTyping,
  onSelect,
}: {
  model: ChatRowModel;
  selected: boolean;
  typing: boolean;
  // Another rep typing a reply in this chat (their email).
  staffTyping?: string;
  onSelect: () => void;
}) {
  const avatarSx = model.urgent ? URGENT_SX : model.avatarSx;
  return (
    <ButtonBase
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      data-testid="chat-row"
      data-chat-id={model.chatId}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: '32px minmax(0, 1fr)',
        columnGap: 1.25,
        alignItems: 'start',
        textAlign: 'left',
        px: 1.5,
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: selected ? '#E6F6FC' : 'background.paper',
        transition: 'background-color .2s',
        '&:hover': { bgcolor: selected ? '#E6F6FC' : 'grey.100' },
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.dark', outlineOffset: -2 },
      }}
    >
      <Box
        aria-hidden="true"
        data-testid="chat-row-avatar"
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 12,
          fontWeight: 600,
          ...avatarSx,
        }}
      >
        {model.avatarText ?? initialsFor(model.label)}
      </Box>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.375 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
          {model.urgent && <Chip size="small" label="Urgent" sx={{ ...pillSx, ...URGENT_SX }} />}
          <Typography
            component="span"
            variant="subtitle2"
            title={model.label}
            sx={{ flex: '0 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {model.label}
          </Typography>
          {/* Where it came from, right after the name (Michael, 10/23). */}
          <ChannelIcon channel={model.source ?? (model.channel === 'app' ? 'app' : 'web')} testId="chat-row-source" />
          <Box sx={{ flex: '1 1 auto' }} />
          {/* Top right, beside the date (Michael, 10/23). */}
          {model.labelled && (
            <Box
              component="span"
              role="img"
              aria-label={`Labelled: ${model.labelled}`}
              title={`Labelled: ${model.labelled}`}
              data-testid="chat-row-labelled"
              sx={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', color: '#4B2FA8' }}
            >
              <LocalOfferIcon sx={{ fontSize: 15 }} />
            </Box>
          )}
          {!model.labelled && model.missingLabel && (
            <Box
              component="span"
              role="img"
              aria-label="Needs a label"
              title="Needs a label"
              data-testid="chat-row-missing-label"
              sx={{ display: 'inline-flex', alignItems: 'center', flex: '0 0 auto', color: '#4B2FA8' }}
            >
              <MissingLabelIcon />
            </Box>
          )}
          {model.timeText && (
            <Typography
              component="span"
              variant="caption"
              data-testid="chat-row-time"
              sx={{ flex: '0 0 auto', fontWeight: 600, px: 1, py: 0.125, borderRadius: 999, whiteSpace: 'nowrap', ...ROW_TONE_SX[model.tone] }}
            >
              {model.timeText}
            </Typography>
          )}
        </Stack>
        {/* Nothing left for this line on a resolved closed row: no empty gap. */}
        {(model.chip || !model.resolution || model.unseen > 0) && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          {model.chip && (
            <Chip
              size="small"
              label={model.chip}
              data-testid="chat-row-turn"
              sx={{ ...pillSx, ...model.chipSx }}
            />
          )}
          {/* Who handled it (Michael, 10/23), here until there's a resolution to sit beside. */}
          {!model.resolution && <HandlerIcon by={model.handler} testId="chat-row-handler" />}
          {/* Never on the chat that's open (Michael, 10/23): it flashed there
              between a new message landing and the open pane stamping it seen. */}
          {model.unseen > 0 && !selected && (
            <Box
              component="span"
              data-testid="chat-row-unseen"
              aria-label={`${model.unseen} unseen ${model.unseen === 1 ? 'message' : 'messages'}`}
              title={`${model.unseen} unseen ${model.unseen === 1 ? 'message' : 'messages'}`}
              sx={{ fontSize: 11, fontWeight: 700, lineHeight: '18px', px: 0.75, borderRadius: 999, bgcolor: '#0B5A86', color: 'common.white' }}
            >
              +{model.unseen}
            </Box>
          )}
          {/* Who's typing, on this line so the preview stays (Michael, 10/26). */}
          {typing ? (
            <Typography component="span" variant="caption" data-testid="chat-row-typing" sx={rowTypingSx('primary.dark')}>
              Customer is typing <TypingDots color="primary.dark" size={4} />
            </Typography>
          ) : staffTyping ? (
            <Typography component="span" variant="caption" data-testid="chat-row-staff-typing" sx={rowTypingSx('grey.700')}>
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {staffTyping} is typing
              </Box>
              <TypingDots size={4} />
            </Typography>
          ) : model.assistantTyping ? (
            <Typography component="span" variant="caption" data-testid="chat-row-assistant-typing" sx={rowTypingSx('#0B5A86')}>
              Assistant is typing <TypingDots color="#0B5A86" size={4} />
            </Typography>
          ) : null}
        </Stack>
        )}
        {(model.preview || model.turnsText) && (
            <Stack direction="row" alignItems="baseline" spacing={1} sx={{ minWidth: 0 }}>
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ flex: '1 1 auto', minWidth: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {model.preview}
              </Typography>
              {/* How long it ran, under the date (Michael, 10/23). */}
              {model.turnsText && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  data-testid="chat-row-turns"
                  sx={{ flex: '0 0 auto', px: 1, whiteSpace: 'nowrap' }}
                >
                  {model.turnsText}
                </Typography>
              )}
            </Stack>
        )}
        {model.detail && (
          <Typography component="span" variant="caption" color="text.secondary" data-testid="chat-row-detail">
            {model.detail}
          </Typography>
        )}
        {model.resolution && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.25 }}>
            {/* One width for every answer, so the icon beside it never moves (Michael, 10/23). */}
            <Chip
              size="small"
              label={model.resolution.text}
              data-testid="chat-row-resolution"
              sx={{ ...pillSx, ...model.resolution.sx, width: RESOLUTION_PILL_WIDTH, justifyContent: 'center' }}
            />
            {/* Who handled it, beside how it went (Michael, 10/23). */}
            <HandlerIcon by={model.handler} testId="chat-row-handler" />
          </Stack>
        )}
        {model.ratings && (
          <Box data-testid="chat-row-ratings" sx={{ mt: 0.25 }}>
            <RatingsStack assistant={model.ratings.assistant} support={model.ratings.support} />
          </Box>
        )}
      </Box>
    </ButtonBase>
  );
}

/**
 * Open chats from the list's point of view: whose turn, how long, and a
 * one-line preview. `listItem` marks a chat known only from the list
 * endpoint (its full fetch hasn't landed): it shows what the list row says.
 */
function activeRowModel(chat: ChatDoc, now: number, listItem?: ChatListItem): ChatRowModel {
  const turn = whoseTurn(chat);
  const urgent = turn === 'support' && isUrgent(chat);
  let timeText = '';
  let tone: RowTone = 'plain';
  if (listItem) {
    const row = listItem;
    return {
      chatId: row.chatId,
      row,
      label: customerLabel(row),
      channel: row.channel,
      chip: WHOSE_TURN_LABEL[turn],
      chipSx: WHOSE_TURN_SX[turn],
      handler: handledBy(row),
      avatarSx: WHOSE_TURN_SX[turn],
      timeText: `${formatDuration(now - new Date(row.lastTurnAt ?? row.createdAt).getTime())} ago`,
      tone,
      urgent: false,
      unseen: 0,
      preview: '',
    };
  }
  if (turn === 'support') {
    const since = waitingSince(chat);
    const waited = since ? now - new Date(since).getTime() : 0;
    timeText = `Waiting ${formatDuration(waited)}`;
    tone = urgent ? 'urgent' : waited >= CHATS_REPLY_TARGET_MS ? 'late' : 'plain';
  } else if (turn === 'customer') {
    const since = quietSince(chat);
    timeText = since ? `Quiet ${formatDuration(now - new Date(since).getTime())}` : '';
  } else {
    timeText = `Started ${formatDuration(now - new Date(chat.createdAt).getTime())} ago`;
  }
  const row = listItemFromDoc(chat);
  return {
    chatId: chat._id,
    row,
    label: customerLabel(row),
    channel: chat.channel,
    chip: WHOSE_TURN_LABEL[turn],
    chipSx: WHOSE_TURN_SX[turn],
    handler: handledBy({ ...row, escalation: chat.escalation, resolvedByStaff: chat.resolvedByStaff }),
    avatarSx: WHOSE_TURN_SX[turn],
    timeText,
    tone,
    urgent,
    unseen: unseenCount(chat),
    assistantTyping: assistantReplying(chat, now),
    preview: lastMessagePreview(chat),
  };
}

// The customer's answer to "Was your issue resolved?", as a pill's colors.
const RESOLUTION_SX: Record<ChatFeedback['resolution'], { bgcolor: string; color: string }> = {
  fully: { bgcolor: '#E9FCD4', color: '#08660D' },
  partially: { bgcolor: '#FFF7CD', color: '#7A4F01' },
  not_at_all: { bgcolor: '#FFE7D9', color: '#7A0C2E' },
};

// Needs labeling rows: the design's purple "needs label" pill.
const LABEL_SX = { bgcolor: '#EFE9FF', color: '#4B2FA8' };

/**
 * Closed rows, filtered to Needs label or not (the same row either way —
 * the dashed tag marks one that needs a label), from the list endpoint
 * alone (no per-chat fetch).
 */
function closedRowModel(item: ChatListItem): ChatRowModel {
  const feedback = item.feedback;
  const when = item.resolvedAt ?? item.lastTurnAt ?? item.createdAt;
  const preview = feedback?.comment
    ? `“${feedback.comment.replace(/\s+/g, ' ').trim()}”`
    : (item.firstMessage ?? '').replace(/\s+/g, ' ').trim();
  // The customer's answer, color-coded (Michael, 10/23): green Fully,
  // yellow Partially, red Not at all, as "Resolution: X" above the stars.
  // No pill up top — every row here is closed already, so "Closed" said nothing.
  const chip = item.status === 'resolved' ? '' : WHOSE_TURN_LABEL[item.status === 'ai' ? 'assistant' : 'support'];
  const chipSx = WHOSE_TURN_SX[item.status === 'ai' ? 'assistant' : 'support'];
  return {
    chatId: item.chatId,
    row: item,
    label: customerLabel(item),
    channel: item.channel,
    chip,
    chipSx,
    handler: handledBy(item),
    turnsText: item.turnCount === 1 ? '1 turn' : `${item.turnCount} turns`,
    // Gray "Resolution: Unrated" over empty stars until the customer answers (Michael, 10/23).
    resolution: feedback
      ? { text: `Resolution: ${CHAT_FEEDBACK_RESOLUTION_LABEL[feedback.resolution]}`, sx: RESOLUTION_SX[feedback.resolution] }
      : item.status === 'resolved'
        ? { text: 'Resolution: Unrated', sx: WHOSE_TURN_SX.customer }
        : undefined,
    ratings: chatRatings(item),
    labelled: item.review ? chatReviewReasonText(item.review.reason) : undefined,
    missingLabel: !!item.reviewKind && !item.review,
    avatarSx: WHOSE_TURN_SX.customer,
    timeText: when ? new Date(when).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '',
    tone: 'plain',
    urgent: false,
    unseen: 0,
    preview,
  };
}

/**
 * A chat in one customer's list (Find a customer, Past chats): when it
 * started, where it stands — "Needs label" until support labels it — the
 * first message, and how it went.
 */
function personRowModel(item: ChatListItem, person: CustomerMatch): ChatRowModel {
  const needsLabel = !!item.reviewKind && !item.review;
  const chip = needsLabel
    ? 'Needs label'
    : item.status === 'resolved'
      ? item.review
        ? 'Labelled'
        : 'Closed'
      : item.status === 'escalated'
        ? 'Escalated'
        : WHOSE_TURN_LABEL.assistant;
  const chipSx = needsLabel
    ? LABEL_SX
    : item.status === 'resolved'
      ? WHOSE_TURN_SX.closed
      : WHOSE_TURN_SX[item.status === 'escalated' ? 'support' : 'assistant'];
  return {
    chatId: item.chatId,
    row: item,
    label: shortChatDate(item.createdAt),
    chip,
    chipSx,
    handler: handledBy(item),
    avatarSx: WHOSE_TURN_SX.customer,
    avatarText: initialsFor(person.label),
    timeText: '',
    tone: 'plain',
    urgent: false,
    unseen: 0,
    preview: (item.firstMessage ?? '').replace(/\s+/g, ' ').trim(),
    detail: chatOutcome(item),
    ratings: chatRatings(item),
    labelled: item.review ? chatReviewReasonText(item.review.reason) : undefined,
    missingLabel: !!item.reviewKind && !item.review,
    source: item.channel === 'app' ? 'app' : 'web',
  };
}

/** The right-hand details column: what the transcript doesn't say at a glance. */
/** "pairing_device" → "Pairing device": the assistant's topic tag, for people. */
const topicText = (topic: string) => {
  const words = topic.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** "2h 5m", "15m", "under a minute": how long a chat ran. */
function chatLength(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'under a minute';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`;
}

function ChatDetails({ chat, myEmail }: { chat: ChatDoc; myEmail?: string }) {
  const firstReply = firstReplyMinutes(chat);
  // No Channel row (Michael, 10/23): the phone or computer by the name says it.
  const rows: { k: string; v: React.ReactNode }[] = [];
  // What it's about and how long it ran, at a glance (Michael, 10/23): the
  // assistant's FIRST topic tag — what the customer came in with; later tags
  // describe where the chat got to ("POM Mobile setup complete") — and why
  // it was flagged urgent.
  const topic = chat.turns.find((t) => t.role === 'agent' && t.topic)?.topic;
  if (topic) rows.push({ k: 'Topic', v: topicText(topic) });
  const urgentTurn = chat.turns.find((t) => t.urgent);
  if (urgentTurn) rows.push({ k: 'Urgent', v: urgentTurn.urgencyReason || 'Flagged by the urgency check' });
  const last = chat.resolvedAt ?? chat.turns[chat.turns.length - 1]?.at ?? chat.createdAt;
  rows.push({
    k: 'Started',
    v: [
      new Date(chat.createdAt).toLocaleString(),
      chatLength(new Date(last).getTime() - new Date(chat.createdAt).getTime()),
      chat.turns.length === 1 ? '1 turn' : `${chat.turns.length} turns`,
    ].join(' · '),
  });
  // The name is the header; how to reach them goes here (pom-2-api #604:
  // web chats show a name now, so the email moved down).
  if (chat.channel === 'app' && chat.userPhoneNumber) rows.push({ k: 'Phone', v: chat.userPhoneNumber });
  if (chat.userEmail && chat.userEmail !== customerLabel(chat)) rows.push({ k: 'Email', v: chat.userEmail });
  // Plain text (Michael, 10/23: no link to the org's settings — rarely what
  // a chat needs, and Customers is a search away).
  if (chat.orgName) rows.push({ k: 'Organization', v: chat.orgName });
  if (chat.escalation?.at) {
    rows.push({
      k: 'Escalated',
      v: `${new Date(chat.escalation.at).toLocaleString()} · ${escalationReasonLabel(chat.escalation.reason)}`,
    });
  }
  if (firstReply !== null) rows.push({ k: 'First reply', v: `${firstReply} min after` });
  if (chat.lastSeenAt) rows.push({ k: 'Customer last seen', v: new Date(chat.lastSeenAt).toLocaleString() });
  if (chat.status === 'resolved') {
    rows.push({
      k: 'Closed',
      v: `${chat.resolvedAt ? `${new Date(chat.resolvedAt).toLocaleString()} · ` : ''}${closedByText(chat, myEmail)}`,
    });
  }
  if (chat.zendeskTicketId) {
    rows.push({
      k: 'Ticket',
      v: (
        <MuiLink href={zendeskTicketUrl(chat.zendeskTicketId)} target="_blank" rel="noopener noreferrer">
          Zendesk #{chat.zendeskTicketId} <OpenInNewIcon fontSize="inherit" sx={{ verticalAlign: 'middle' }} />
        </MuiLink>
      ),
    });
  }
  return (
    <Box component="section" aria-labelledby="chat-details-heading" data-testid="chat-details">
      <Typography
        id="chat-details-heading"
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, lineHeight: 2 }}
      >
        Details
      </Typography>
      <Stack spacing={1}>
        {rows.map((r) => (
          <Box key={r.k}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {r.k}
            </Typography>
            <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
              {r.v}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * POM Master "Chats" tab (Ticket "AI chat escalation v1", 9/9) — the inbox
 * over the persisted `supportAgentChats` collection, sibling to Activity.
 * Every chat persists here, including practice chats from this same POM
 * Master's own widget (Carlos, 9/9) — there is no separate "test" store.
 * List/drawer structure follows activity-panel.tsx; the transcript
 * rendering follows ai-chat-widget.tsx's display rules (urgent/skipped/
 * empty derive from the turn's flags, never from `body` alone).
 */
/**
 * `hidden`: another Support Agent tab is showing. The panel stays mounted so
 * the open chat, a draft and an in-flight reply or label survive a tab switch
 * (attack/defend 10/26), but it stamps nothing "seen" while hidden.
 */
export const ChatsPanel: React.FC<{ hidden?: boolean }> = ({ hidden = false }) => {
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;
  // Deep link into a specific chat (locked brief, item C; ClickUp 868mb93wj)
  // — `chat=<chatId>` in the URL, kept in sync as its own param alongside
  // `tab` (support-agent/index.tsx owns `tab`; this panel owns `chat` since
  // it's the only place that ever opens/closes the drawer).
  const [searchParams, setSearchParams] = useSearchParams();
  // The rep using this inbox (their own typing and replies read "you").
  const myEmail = signedInEmail();
  const [view, setView] = useState<ChatsView>('active');
  // Hide test chats (Michael, 10/28): the dev inbox lists every stage's
  // chats, so this asks for prod's only (pom-2-api #621's `prodOnly`).
  // Remembered per browser. The switch shows once a test chat has been
  // listed, or when it starts on, so prod's inbox, which only lists its own
  // chats, never shows it. Once shown it stays (Michael, 10/29: turned off
  // on Active, with no open test chat, it vanished).
  const [hideTestChats, setHideTestChats] = useState(readHideTestChats);
  const hideTestChatsRef = useRef(hideTestChats);
  hideTestChatsRef.current = hideTestChats;
  const [showTestChatsSwitch, setShowTestChatsSwitch] = useState(hideTestChats);
  const queueFilter = () => (hideTestChatsRef.current ? { prodOnly: true } : {});
  // Needs review / Closed show this many pages of CHATS_VIEW_PAGE_SIZE;
  // "Show more" adds one. Paged, so every closed chat stays reachable.
  const [viewPages, setViewPages] = useState(1);
  // What is on screen now, for loads started from an older render (a doorbell
  // timer, a slow resolve): whichever load runs last loads the current view
  // (attack/defend round 2).
  const viewRef = useRef(view);
  viewRef.current = view;
  const viewPagesRef = useRef(viewPages);
  viewPagesRef.current = viewPages;
  // Every open chat, in full (whose turn it is needs the turns), by id.
  const [activeChats, setActiveChats] = useState<Record<string, ChatDoc>>({});
  const activeChatsRef = useRef(activeChats);
  activeChatsRef.current = activeChats;
  // The open chats as the list endpoint last returned them — a row shows
  // from this until (or if) its full fetch lands.
  const [activeItems, setActiveItems] = useState<Record<string, ChatListItem>>({});
  // Open chats whose first full fetch is in flight, by how many fetches are
  // (Michael, 10/23: a row drawn from the list alone read "Support's turn"
  // for a moment before it became "Customer's turn" — the list doesn't say
  // who spoke last). Their rows wait for it; a failed fetch shows the row
  // from the list after all.
  const [firstFetches, setFirstFetches] = useState<Record<string, number>>({});
  const countFirstFetches = (ids: string[], by: 1 | -1) => {
    if (ids.length === 0) return;
    setFirstFetches((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        const n = (next[id] ?? 0) + by;
        if (n > 0) next[id] = n;
        else delete next[id];
      });
      return next;
    });
  };
  // Open chats the list endpoint holds beyond what was fetched (only past
  // CHATS_ACTIVE_MAX_PAGES pages of one status) — said, never silently cut.
  const [activeOverflow, setActiveOverflow] = useState(0);
  // Needs review / Closed rows, tagged with their view: a response for the
  // view the user just left never shows (or gets relabelled) under the new one.
  const [viewList, setViewList] = useState<{ view: ChatsView; items: ChatListItem[]; total: number }>({
    view: 'active',
    items: [],
    total: 0,
  });
  // How many closed chats wait for a label (the Needs labeling count).
  const [labelTotal, setLabelTotal] = useState<number | null>(null);
  // Each list view's last rows, so a switch shows something at once
  // (Michael, 10/23: the Needs label toggle "takes a second to load") and
  // the load behind it refreshes quietly instead of with a spinner.
  const listCacheRef = useRef<Partial<Record<ChatsView, { items: ChatListItem[]; total: number }>>>({});
  const prefilledRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Which update to a chat is newest (attack/defend round 1, A1-1): a list
  // refresh snapshots the open chats, then fetches; anything upsertActive
  // applied to a chat after that snapshot (a doorbell's refetch, a reply, a
  // resolve) is newer than what the refresh holds, so the refresh leaves that
  // chat alone instead of rolling it back, dropping it or reviving it.
  const touchSeqRef = useRef(0);
  const touchedAtRef = useRef(new Map<string, number>());
  const loadSeqRef = useRef(0);

  // A chat as the server returned it: kept in the Active list while it is
  // open, dropped once it is resolved. `id` is the one we asked for.
  // Per chat, which read is newest (Carlos's #585 review: a slow poll read
  // landing after the doorbell's took the new message back out for up to
  // 60 s): a read applies only if no later-started read of that chat has
  // applied already — "latest applied", so a later read still out (or one
  // that fails) never throws away an earlier good one (Fable round, 10/26).
  // Actions keep their own guards: requestSeqRef for the open chat,
  // touchedAtRef for list rows.
  const chatReadSeqRef = useRef(new Map<string, number>());
  const appliedReadRef = useRef(new Map<string, number>());
  const startChatRead = (id: string) => {
    const n = (chatReadSeqRef.current.get(id) ?? 0) + 1;
    chatReadSeqRef.current.set(id, n);
    return n;
  };
  // When an action (reply, resolve, archive retry, label) last changed each
  // chat, on the touch clock: a read that left before it is older than what
  // the action knows (Fable pass 2, 10/26: reopening a chat mid-reply showed
  // the transcript without the reply). Actions still never touch the read order.
  const actedAtRef = useRef(new Map<string, number>());
  const markActed = (id: string) => actedAtRef.current.set(id, ++touchSeqRef.current);
  /** Claims the apply for read `n` of chat `id`; false when a later read already applied. */
  const applyChatRead = (id: string, n: number) => {
    if (n <= (appliedReadRef.current.get(id) ?? 0)) return false;
    appliedReadRef.current.set(id, n);
    return true;
  };
  const upsertActive = (chat: ChatDoc, id: string = chat._id) => {
    touchedAtRef.current.set(id, ++touchSeqRef.current);
    const drop = <T,>(prev: Record<string, T>) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    };
    // A test chat's doorbell (websocket-server rings this stage's chats)
    // never adds it back while they're hidden.
    if (chat.status === 'resolved' || (hideTestChatsRef.current && isTestChat(chat))) {
      setActiveChats(drop);
      setActiveItems(drop);
      return;
    }
    setActiveChats((prev) => ({ ...prev, [id]: { ...chat, _id: id } }));
  };

  // Brings the Active list in line with the open chats the list endpoint
  // returned (snapshot taken at `mark`): rows show at once from the list,
  // chats it no longer lists drop, and only the new or changed ones (status,
  // turn count, or still "Not opened yet" — someone else may have opened it,
  // which no doorbell says) are fetched in full.
  const refreshActive = async (open: ChatListItem[], mark: number, loadSeq: number, forView: ChatsView) => {
    const current = activeChatsRef.current;
    // The open chat refreshes itself (fetchDetail), and keeps its row in step.
    const openChatId = selectedRowRef.current?.chatId;
    const newer = (id: string) => (touchedAtRef.current.get(id) ?? 0) > mark;
    const listed = new Set(open.map((item) => item.chatId));
    setActiveItems((prev) => {
      const next: Record<string, ChatListItem> = {};
      open.forEach((item) => {
        // Resolved since the snapshot (dropped by upsertActive): stays out.
        if (!newer(item.chatId) || item.chatId in prev) next[item.chatId] = item;
      });
      Object.keys(prev).forEach((id) => {
        if (!listed.has(id) && newer(id)) next[id] = prev[id];
      });
      return next;
    });
    const stale = open.filter((item) => {
      const known = current[item.chatId];
      if (item.chatId === openChatId && known) return false;
      return (
        !known ||
        known.status !== item.status ||
        known.turns.length !== item.turnCount ||
        // Only Active shows it, and doorbell loads in the other views would
        // otherwise refetch every unopened chat each time (round 2).
        (forView === 'active' && notOpenedYet(known))
      );
    });
    const firstTime = stale.filter((item) => !current[item.chatId]).map((item) => item.chatId);
    countFirstFetches(firstTime, 1);
    const fetched = await Promise.all(
      stale.map((item) =>
        Promise.resolve()
          .then(() => supportAgentApi.getChat(item.chatId))
          .then((chat) => ({ id: item.chatId, chat }))
          .catch(() => null),
      ),
    );
    countFirstFetches(firstTime, -1);
    if (loadSeq !== loadSeqRef.current) return; // a later refresh owns the list now
    setActiveChats((prev) => {
      const next: Record<string, ChatDoc> = {};
      Object.keys(prev).forEach((id) => {
        if (listed.has(id) || newer(id)) next[id] = prev[id];
      });
      fetched.forEach((entry) => {
        if (!entry || !entry.chat || !listed.has(entry.id) || newer(entry.id)) return;
        if (entry.chat.status === 'resolved') delete next[entry.id];
        else next[entry.id] = { ...entry.chat, _id: entry.id };
      });
      return next;
    });
  };

  // Every open chat of one status: page 1, then the rest up to a cap.
  const listOpen = async (status: 'escalated' | 'ai') => {
    const first = await supportAgentApi.listChats({ status, page: 1, limit: CHATS_ACTIVE_LIMIT, ...queueFilter() });
    const pages = Math.min(Math.ceil(first.total / CHATS_ACTIVE_LIMIT), CHATS_ACTIVE_MAX_PAGES);
    const rest = await Promise.all(
      Array.from({ length: Math.max(0, pages - 1) }, (_, i) =>
        supportAgentApi.listChats({ status, page: i + 2, limit: CHATS_ACTIVE_LIMIT, ...queueFilter() }),
      ),
    );
    const items = [first, ...rest].flatMap((res) => res.items).filter((item) => item.status === status);
    return { items, missing: Math.max(0, first.total - first.items.length - rest.reduce((n, r) => n + r.items.length, 0)) };
  };

  // `quiet`: a background refresh (poll tick, live doorbell) keeps the rows
  // on screen — the spinner is for the user's own view change, not a flash
  // every 15 s.
  const load = async ({ quiet = false }: { quiet?: boolean } = {}) => {
    const seq = ++loadSeqRef.current;
    const mark = touchSeqRef.current;
    const forView = viewRef.current;
    const pages = viewPagesRef.current;
    if (!quiet) setLoading(true);
    setError(null);
    // The count on Needs labeling, from wherever the user is: on its own,
    // so a slow count never holds up the list. Best-effort.
    if (forView !== 'label') {
      Promise.resolve()
        .then(() => supportAgentApi.listChats({ needsLabel: true, page: 1, limit: 1, ...queueFilter() }))
        .then((res) => {
          if (seq === loadSeqRef.current && typeof res?.total === 'number') setLabelTotal(res.total);
        })
        .catch(() => undefined);
    }
    try {
      const [escalated, ai, viewPagesRes] = await Promise.all([
        listOpen('escalated'),
        listOpen('ai'),
        forView === 'active'
          ? Promise.resolve(null)
          : Promise.all(
              Array.from({ length: pages }, (_, i) =>
                supportAgentApi.listChats({
                  ...(forView === 'label' ? { needsLabel: true } : { status: 'resolved' as const }),
                  page: i + 1,
                  limit: CHATS_VIEW_PAGE_SIZE,
                  ...queueFilter(),
                }),
              ),
            ),
      ]);
      if (seq !== loadSeqRef.current) return;
      const listed = [...escalated.items, ...ai.items, ...(viewPagesRes ?? []).flatMap((res) => res.items)];
      if (listed.some(isTestChat)) setShowTestChatsSwitch(true);
      setActiveOverflow(escalated.missing + ai.missing);
      if (forView === 'label') setLabelTotal(viewPagesRes?.[0]?.total ?? null);
      if (viewPagesRes) {
        const seen = new Set<string>();
        const items = viewPagesRes
          .flatMap((res) => res.items)
          .filter((item) => (seen.has(item.chatId) ? false : (seen.add(item.chatId), true)));
        const total = viewPagesRes[0]?.total ?? items.length;
        listCacheRef.current[forView] = { items, total };
        setViewList({ view: forView, items, total });
      }
      await refreshActive([...escalated.items, ...ai.items], mark, seq, forView);
    } catch (err: any) {
      if (seq === loadSeqRef.current) setError(err?.response?.data?.message || LIST_ERROR_COPY);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load({ quiet: prefilledRef.current });
    prefilledRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, viewPages]);

  // To another view, from what is already known: its own last rows, or —
  // turning Needs label on for the first time — Closed's rows that need a
  // label, until the full list (every closed chat waiting, not only the
  // loaded pages) lands behind them.
  const switchView = (next: ChatsView) => {
    const cache = listCacheRef.current;
    const needsLabel = (item: ChatListItem) => !!item.reviewKind && !item.review;
    // None of the loaded ones need a label says nothing about the rest: the spinner, not "Nothing to label".
    const filtered = next === 'label' && cache.closed ? cache.closed.items.filter(needsLabel) : [];
    const fromClosed = filtered.length ? filtered : undefined;
    const instant = cache[next] ?? (fromClosed ? { items: fromClosed, total: labelTotal ?? fromClosed.length } : undefined);
    if (instant) {
      setViewList({ view: next, ...instant });
      prefilledRef.current = true;
    }
    setView(next);
    setViewPages(1);
  };

  // The list's relative times ("Waiting 12m") move on without a refetch.
  const [now, setNow] = useState(() => Date.now());
  const wideForDetails = useMediaQuery('(min-width:1400px)');
  const [detailsChoice, setDetailsChoice] = useState<boolean | null>(null);

  // ── detail drawer ───────────────────────────────────────────────────
  const [selectedRow, setSelectedRow] = useState<ChatListItem | null>(null);
  const selectedRowRef = useRef<ChatListItem | null>(null);
  useEffect(() => {
    selectedRowRef.current = selectedRow;
  }, [selectedRow]);

  const [detail, setDetail] = useState<ChatDoc | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  // Pollen review, PR #540, low item: a chat id from a stale/malformed
  // Zendesk link 404s — distinct from a transient load failure (worth
  // retrying) both in copy and in behavior: polling for a chat id that
  // doesn't exist would just repeat the same 404 forever.
  const [chatNotFound, setChatNotFound] = useState(false);
  const chatNotFoundRef = useRef(false);
  useEffect(() => {
    chatNotFoundRef.current = chatNotFound;
  }, [chatNotFound]);
  const [replyInput, setReplyInput] = useState('');
  // Which chats have a send / resolve / archive retry in flight, by id
  // (Carlos's #585 review: one panel-wide flag showed "Sending…",
  // "Resolving…" or "Retrying…" on whatever chat was open, and refocused its
  // reply box when another chat's send finished).
  const [replyingIn, setReplyingIn] = useState<Record<string, true>>({});
  const [replyError, setReplyError] = useState<string | null>(null);
  const replyInputRef = useRef<HTMLInputElement | null>(null);
  const [resolvingIn, setResolvingIn] = useState<Record<string, true>>({});
  const [resolveError, setResolveError] = useState<string | null>(null);
  // Page-level notice (outside the Drawer). `persistent` notices stay until
  // dismissed — used for the outcome of an action on a chat that is no longer
  // open, which has nowhere else to show and names that chat's customer.
  const [resolveSnackbar, setResolveSnackbar] = useState<{ message: string; persistent?: boolean } | null>(null);
  const [archivingIn, setArchivingIn] = useState<Record<string, true>>({});
  // The open chat's own: what its buttons and reply box show.
  const replySubmitting = !!selectedRow && !!replyingIn[selectedRow.chatId];
  const resolveSubmitting = !!selectedRow && !!resolvingIn[selectedRow.chatId];
  const archiveSubmitting = !!selectedRow && !!archivingIn[selectedRow.chatId];
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // Race-guard: two quick row clicks fire two overlapping GETs — a resolving
  // request only applies if it's still the most recently issued one.
  const requestSeqRef = useRef(0);
  // Separate from requestSeqRef (Pollen review, 10/14): requestSeqRef is also
  // bumped by a completed action itself (to invalidate a stale in-flight
  // poll — see handleReply's comment below), so comparing requestSeqRef in
  // isStillSelected broke two actions overlapping on the SAME open chat —
  // e.g. Resolve is slow, a reply sent meanwhile lands first and bumps the
  // seq, then Resolve sees the seq changed and wrongly takes the stale path.
  // drawerGenRef only changes when the drawer itself opens or closes.
  const drawerGenRef = useRef(0);

  // The transcript opens at the latest turn and follows new ones (and the
  // typing dots), unless the agent has scrolled up to reread. The layout
  // effect that does the scrolling sits further down, next to the typing
  // state it also follows; the refs live here because closeDrawer and
  // handleReply below reset them.
  const transcriptRef = useRef<HTMLDivElement | null>(null);
  // The chat pane and its heading — focus targets once Close or Resolve
  // removes the button that had it.
  const paneRef = useRef<HTMLElement | null>(null);
  const listNavRef = useRef<HTMLElement | null>(null);
  const paneHeadingRef = useRef<HTMLElement | null>(null);
  const transcriptAtBottomRef = useRef(true);
  const transcriptChatRef = useRef<string | null>(null);
  const transcriptStatusRef = useRef<ChatStatus | null>(null);
  // The chat column: a closed chat scrolls here, as one piece.
  const paneColumnRef = useRef<HTMLDivElement | null>(null);

  const fetchDetail = async (chatId: string, seq: number) => {
    const read = startChatRead(chatId);
    const mark = touchSeqRef.current;
    try {
      const chat = await supportAgentApi.getChat(chatId);
      if (seq !== requestSeqRef.current || !chat) return;
      // A later read of this chat already applied: it wins.
      if (!applyChatRead(chatId, read)) return;
      // An action changed this chat after the read left: read it again
      // rather than show what came before the action.
      if ((actedAtRef.current.get(chatId) ?? 0) > mark) {
        void fetchDetail(chatId, requestSeqRef.current);
        return;
      }
      // The list row follows the same fetch (whose turn, the preview).
      upsertActive(chat, chatId);
      // A reply clears the rep's typing stamp without a typing event.
      clearStaffTypingIfReplied(chat, chatId);
      // Same chat: a live chat_seen that landed while this fetch was in
      // flight must not be undone by this now-stale response.
      setDetail((current) =>
        current && current._id === chat._id
          ? { ...chat, lastSeenAt: laterLastSeenAt(current.lastSeenAt, chat.lastSeenAt) }
          : chat,
      );
      // One failed background refetch must not leave "Could not load" on a
      // conversation that the next tick loaded fine.
      setDetailError(null);
    } catch (err: any) {
      if (seq !== requestSeqRef.current) return;
      if (err?.response?.status === 404) {
        setChatNotFound(true);
      } else if (read > (appliedReadRef.current.get(chatId) ?? 0)) {
        // Not when a later read already loaded it fine (Fable pass 2, 10/26).
        setDetailError(DETAIL_ERROR_COPY);
      }
    } finally {
      if (seq === requestSeqRef.current) setDetailLoading(false);
    }
  };

  const openDrawer = (row: ChatListItem) => {
    // The open chat's own row (attack/defend A1-3): the split view keeps it
    // clickable, but re-opening would wipe the draft, send typing:false and
    // make an in-flight reply or resolve look stale. Just refresh it.
    if (selectedRowRef.current?.chatId === row.chatId) {
      if (!chatNotFoundRef.current) void fetchDetail(row.chatId, requestSeqRef.current);
      return;
    }
    stopTyping();
    const seq = ++requestSeqRef.current;
    ++drawerGenRef.current;
    setSelectedRow(row);
    // An open chat the Active list already holds in full shows at once; the
    // fetch below still brings the server's latest.
    const cached = activeChatsRef.current[row.chatId];
    setDetail(cached ?? null);
    setDetailError(null);
    setChatNotFound(false);
    setDetailLoading(!cached);
    setReplyInput('');
    setReplyError(null);
    setResolveError(null);
    setArchiveError(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('chat', row.chatId);
    setSearchParams(nextParams, { replace: true });
    void fetchDetail(row.chatId, seq);
  };

  const closeDrawer = () => {
    // Keyboard focus (attack/defend A3-5): Close sits inside the pane that is
    // about to empty, so a keyboard user goes back to the chat's own row.
    const closingId = selectedRowRef.current?.chatId;
    const focusWasInPane = !!paneRef.current?.contains(document.activeElement);
    stopTyping();
    requestSeqRef.current += 1; // invalidate any in-flight detail fetch
    drawerGenRef.current += 1;
    // The transcript element goes away with `detail`; re-opening the same
    // chat must count as a switch, or it would land at the top and not
    // follow new turns when the agent had scrolled up before closing
    // (Interrogator, #562 review).
    transcriptChatRef.current = null;
    transcriptAtBottomRef.current = true;
    setSelectedRow(null);
    setDetail(null);
    setDetailError(null);
    setChatNotFound(false);
    setReplyError(null);
    setResolveError(null);
    setArchiveError(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('chat');
    setSearchParams(nextParams, { replace: true });
    if (closingId && focusWasInPane) {
      window.requestAnimationFrame(() => {
        // Its row may be gone (resolved): then the first row, or the list.
        const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="chat-row"]'));
        (rows.find((el) => el.dataset.chatId === closingId) ?? rows[0] ?? listNavRef.current)?.focus();
      });
    }
  };

  // Deep link into a specific chat (locked brief, item C): on mount, if the
  // URL already carries `chat=<id>` (a Zendesk ticket's link, or a page
  // refresh with the drawer open), open it directly by id — via
  // `getChat`, not the paginated list, so this works regardless of the
  // chat's status/page/filter. Synthesizes the `ChatListItem` shape
  // `openDrawer` expects from the full `ChatDoc` response (both carry the
  // same identifying fields — see support-agent-api.ts). Runs once; a row
  // click after this always goes through the normal `openDrawer(row)`.
  useEffect(() => {
    const initialChatId = searchParams.get('chat');
    if (!initialChatId) return;

    const seq = ++requestSeqRef.current;
    ++drawerGenRef.current;
    setSelectedRow({
      chatId: initialChatId,
      userId: '',
      status: 'ai',
      turnCount: 0,
      lastTurnAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    });
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);

    // In the chat's read order like every other read (Fable pass 2, 10/26):
    // a doorbell read that lands first is newer than this one.
    const read = startChatRead(initialChatId);
    (async () => {
      try {
        const chat = await supportAgentApi.getChat(initialChatId);
        if (seq !== requestSeqRef.current) return;
        if (applyChatRead(initialChatId, read)) setDetail(chat);
        setSelectedRow({
          chatId: chat._id,
          customerId: chat.customerId,
          userId: chat.userId,
          userEmail: chat.userEmail,
          status: chat.status,
          turnCount: chat.turns.length,
          lastTurnAt: chat.turns[chat.turns.length - 1]?.at ?? chat.createdAt,
          createdAt: chat.createdAt,
          resolvedAt: chat.resolvedAt,
          zendeskTicketId: chat.zendeskTicketId,
          attachmentCount: chat.turns.reduce((n, turn) => n + (turn.attachments?.length ?? 0), 0),
        });
      } catch (err: any) {
        if (seq !== requestSeqRef.current) return;
        if (err?.response?.status === 404) {
          setChatNotFound(true);
        } else if (read > (appliedReadRef.current.get(initialChatId) ?? 0)) {
          setDetailError(DETAIL_ERROR_COPY);
        }
      } finally {
        if (seq === requestSeqRef.current) setDetailLoading(false);
      }
    })();
    // Mount-only — a subsequent row click drives selection through
    // openDrawer/closeDrawer instead, same as before this deep-link support existed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 15s background refresh (Carlos, 9/9: poll, no WebSockets) — reloads the
  // list (so a newly-escalated chat shows up) and, if a chat is open, that
  // chat's transcript (so a reply typed from elsewhere shows up).
  // Live updates (live-chat-socket.ts): held open while this inbox is
  // mounted. A chat_update refetches just the chats it names — the open one
  // through fetchDetail, any other into its list row — so a busy inbox is
  // one small GET per changed chat, not a reload of every list.
  // The split view's height: from where it starts to the bottom of the
  // screen, measured (the old guess, 90vh minus a fixed header allowance,
  // left blank page under it on taller screens). Re-measured on resize and
  // when the error banner above it comes or goes.
  const splitRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [splitHeight, setSplitHeight] = useState<number | null>(null);
  // The page layout's own padding under every page (the main area's, the
  // tab container's), taken back here (Michael, 10/23: "why do I have to
  // scroll down just to get to something where I have to scroll down
  // again") so the page itself never scrolls — only the list and the chat.
  const [pagePaddingBelow, setPagePaddingBelow] = useState(0);
  const measureSplit = () => {
    const el = splitRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY;
    const next = Math.max(CHATS_SPLIT_MIN_HEIGHT, Math.round(window.innerHeight - top - CHATS_SPLIT_BOTTOM_GAP));
    setSplitHeight((current) => (current === next ? current : next));
    let below = 0;
    for (let a = panelRef.current?.parentElement; a && a !== document.body; a = a.parentElement) {
      const style = window.getComputedStyle(a);
      below += (parseFloat(style.paddingBottom) || 0) + (parseFloat(style.borderBottomWidth) || 0);
    }
    setPagePaddingBelow((current) => (current === below ? current : below));
  };
  useEffect(() => {
    window.addEventListener('resize', measureSplit);
    return () => window.removeEventListener('resize', measureSplit);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    measureSplit();
  }, [error]); // eslint-disable-line react-hooks/exhaustive-deps
  // The page around the inbox can move after it mounts (the page header
  // tightening itself up): re-measure whenever the document's size changes.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => measureSplit());
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const liveConnected = useLiveChatSocket(true);
  const [everConnected, setEverConnected] = useState(false);
  const [connectGraceOver, setConnectGraceOver] = useState(false);
  useEffect(() => {
    if (liveConnected) setEverConnected(true);
  }, [liveConnected]);
  useEffect(() => {
    const timer = window.setTimeout(() => setConnectGraceOver(true), CHATS_LIVE_CONNECT_GRACE_MS);
    return () => window.clearTimeout(timer);
  }, []);
  // Live; lost after it was live; never up past the grace period; or nothing yet.
  const liveStatus: 'live' | 'reconnecting' | 'connecting' | null = liveConnected
    ? 'live'
    : everConnected
      ? 'reconnecting'
      : connectGraceOver
        ? 'connecting'
        : null;
  const liveRefreshTimerRef = useRef<number | null>(null);
  const liveDetailChatIdsRef = useRef(new Set<string>());
  const refreshOne = async (chatId: string) => {
    const read = startChatRead(chatId);
    // Like refreshActive: a reply or resolve applied to this row after the
    // read started is newer than what it read (Fable round, 10/26).
    const mark = touchSeqRef.current;
    try {
      const chat = await supportAgentApi.getChat(chatId);
      if (!chat) return;
      if ((touchedAtRef.current.get(chatId) ?? 0) > mark) return;
      if (!applyChatRead(chatId, read)) return;
      upsertActive(chat, chatId);
      const lastRole = chat.turns[chat.turns.length - 1]?.role;
      if (lastRole === 'customer') clearCustomerTypingFor(chatId);
      clearStaffTypingIfReplied(chat, chatId);
    } catch {
      // The next poll tick catches it up.
    }
  };
  // The open chat refetches the moment its doorbell rings (Michael, 10/26:
  // a customer's message reached the inbox ~0.3 s late — all of it the
  // debounce below — while a rep's reply reached the widget at once). One
  // fetch at a time; a ring during it fetches once more after.
  const openFetchRef = useRef<{ chatId: string; again: boolean } | null>(null);
  const fetchOpenNow = (chatId: string) => {
    const busy = openFetchRef.current;
    if (busy && busy.chatId === chatId) {
      busy.again = true;
      return;
    }
    // Only this call's own entry is cleared: another chat opened since has
    // its own (attack/defend 10/26).
    const entry = { chatId, again: false };
    openFetchRef.current = entry;
    void fetchDetail(chatId, requestSeqRef.current).finally(() => {
      if (openFetchRef.current !== entry) return;
      openFetchRef.current = null;
      if (entry.again && selectedRowRef.current?.chatId === chatId && !chatNotFoundRef.current) fetchOpenNow(chatId);
    });
  };
  const doorbellReloadRef = useRef({ busy: false, again: false });
  const reloadForDoorbell = () => {
    const state = doorbellReloadRef.current;
    if (state.busy) {
      state.again = true;
      return;
    }
    state.busy = true;
    void load({ quiet: true }).finally(() => {
      state.busy = false;
      if (state.again) {
        state.again = false;
        if (viewRef.current !== 'active') reloadForDoorbell();
      }
    });
  };
  useLiveChatEvent(true, 'chat_update', (event) => {
    const row = selectedRowRef.current;
    if (row && row.chatId === event.chatId && !chatNotFoundRef.current) {
      fetchOpenNow(event.chatId);
      // The rest of the list (Closed, a customer's chats) still refreshes below.
      if (viewRef.current === 'active' && !personRef.current) return;
    } else {
      liveDetailChatIdsRef.current.add(event.chatId);
    }
    if (liveRefreshTimerRef.current !== null) return;
    liveRefreshTimerRef.current = window.setTimeout(() => {
      liveRefreshTimerRef.current = null;
      const touched = liveDetailChatIdsRef.current;
      liveDetailChatIdsRef.current = new Set();
      const row = selectedRowRef.current;
      touched.forEach((chatId) => {
        if (row && row.chatId === chatId) {
          if (!chatNotFoundRef.current) void fetchDetail(chatId, requestSeqRef.current);
        } else {
          void refreshOne(chatId);
        }
      });
      // Needs labeling / Closed are plain lists: a change can add or move a
      // row. One reload at a time, then one more if a ring came during it
      // (Carlos's #585 review: slow reloads stacked up in parallel).
      if (viewRef.current !== 'active') reloadForDoorbell();
      if (personRef.current) {
        if (personLoadRef.current.busy) personLoadRef.current.again = true;
        else setPersonRefresh((n) => n + 1);
      }
    }, CHATS_LIVE_REFRESH_DEBOUNCE_MS);
  });
  useEffect(
    () => () => {
      if (liveRefreshTimerRef.current !== null) window.clearTimeout(liveRefreshTimerRef.current);
    },
    [],
  );

  // "Customer is typing" (Michael, 10/10): per chat, so the list can show it
  // on every row (websocket-server already sends it to every POM Master),
  // for CHATS_CUSTOMER_TYPING_TTL_MS per note; typing:false or their message
  // landing ends it. Each chat has its own timer — one chat's ping or stop
  // never touches another's (Michael review, 10/13).
  const [customerTyping, setCustomerTyping] = useState<Record<string, true>>({});
  const customerTypingTimersRef = useRef(new Map<string, number>());
  const clearCustomerTypingFor = (chatId: string) => {
    const timer = customerTypingTimersRef.current.get(chatId);
    if (timer !== undefined) window.clearTimeout(timer);
    customerTypingTimersRef.current.delete(chatId);
    setCustomerTyping((current) => {
      if (!current[chatId]) return current;
      const next = { ...current };
      delete next[chatId];
      return next;
    });
  };
  useLiveChatEvent(true, 'chat_customer_typing', (event) => {
    if (!event.typing) {
      if (!customerTypingTimersRef.current.has(event.chatId)) return;
      window.clearTimeout(customerTypingTimersRef.current.get(event.chatId));
      customerTypingTimersRef.current.set(
        event.chatId,
        window.setTimeout(() => clearCustomerTypingFor(event.chatId), CHATS_CUSTOMER_TYPING_STOP_GRACE_MS),
      );
      return;
    }
    setCustomerTyping((current) => (current[event.chatId] ? current : { ...current, [event.chatId]: true }));
    const timer = customerTypingTimersRef.current.get(event.chatId);
    if (timer !== undefined) window.clearTimeout(timer);
    customerTypingTimersRef.current.set(
      event.chatId,
      window.setTimeout(() => clearCustomerTypingFor(event.chatId), CHATS_CUSTOMER_TYPING_TTL_MS),
    );
  });
  useEffect(
    () => () => {
      customerTypingTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      customerTypingTimersRef.current.clear();
    },
    [],
  );
  // Another rep typing a reply (websocket-server #78's chat_staff_typing,
  // Michael 10/23): per chat, by their email, so two reps don't answer the
  // same customer at once. This rep's own typing comes back too and is left
  // out. Ends on typing:false, their reply landing, or after
  // CHATS_STAFF_TYPING_TTL_MS without a ping.
  const [staffTyping, setStaffTyping] = useState<Record<string, string>>({});
  const staffTypingTimersRef = useRef(new Map<string, number>());
  // When each rep's typing started (server time), so a refetch clears it only
  // for a reply that landed after (attack/defend 10/26: any refetch of a chat
  // whose last turn was an older reply wiped it).
  const staffTypingSinceRef = useRef(new Map<string, number>());
  const clearStaffTypingFor = (chatId: string) => {
    const timer = staffTypingTimersRef.current.get(chatId);
    if (timer !== undefined) window.clearTimeout(timer);
    staffTypingTimersRef.current.delete(chatId);
    staffTypingSinceRef.current.delete(chatId);
    setStaffTyping((current) => {
      if (!current[chatId]) return current;
      const next = { ...current };
      delete next[chatId];
      return next;
    });
  };
  const clearStaffTypingIfReplied = (chat: ChatDoc, chatId: string) => {
    const since = staffTypingSinceRef.current.get(chatId);
    if (since === undefined) return;
    const replied = chat.turns.some((t) => t.role === 'human' && new Date(t.at).getTime() >= since);
    if (replied) clearStaffTypingFor(chatId);
  };
  useLiveChatEvent(true, 'chat_staff_typing', (event) => {
    if (!event.typing) {
      clearStaffTypingFor(event.chatId);
      return;
    }
    const by = typeof event.by === 'string' && event.by ? event.by : null;
    if (!by || (myEmail && by === myEmail)) return;
    if (!staffTypingSinceRef.current.has(event.chatId)) {
      const at = event.at ? new Date(event.at).getTime() : NaN;
      staffTypingSinceRef.current.set(event.chatId, Number.isNaN(at) ? Date.now() : at);
    }
    setStaffTyping((current) => (current[event.chatId] === by ? current : { ...current, [event.chatId]: by }));
    const timer = staffTypingTimersRef.current.get(event.chatId);
    if (timer !== undefined) window.clearTimeout(timer);
    staffTypingTimersRef.current.set(
      event.chatId,
      window.setTimeout(() => clearStaffTypingFor(event.chatId), CHATS_STAFF_TYPING_TTL_MS),
    );
  });
  useEffect(
    () => () => {
      staffTypingTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      staffTypingTimersRef.current.clear();
    },
    [],
  );

  // Find a customer (Michael, 10/21; pom-2-api #604's `search`): the people
  // whose chats match, grouped by person, CHATS_SEARCH_DEBOUNCE_MS after the
  // last keystroke. Picking one swaps the split view for their history.
  const [searchText, setSearchText] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  // `capped`: more chats matched than one search returns — said, never silently cut (attack/defend 10/26).
  const [searchResult, setSearchResult] = useState<{ text: string; people: CustomerMatch[]; failed: boolean; capped?: boolean } | null>(null);
  const searchSeqRef = useRef(0);
  const searchTrimmed = searchText.trim();
  useEffect(() => {
    const seq = ++searchSeqRef.current;
    if (searchTrimmed.length < CHATS_SEARCH_MIN_CHARS) {
      setSearchResult(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      Promise.resolve()
        .then(() => supportAgentApi.listChats({ search: searchTrimmed, limit: CHATS_SEARCH_LIMIT, ...queueFilter() }))
        .then((res) => {
          if (seq === searchSeqRef.current) {
            const items = res?.items ?? [];
            setSearchResult({
              text: searchTrimmed,
              people: groupByCustomer(items),
              failed: false,
              capped: typeof res?.total === 'number' && res.total > items.length,
            });
          }
        })
        .catch(() => {
          if (seq === searchSeqRef.current) setSearchResult({ text: searchTrimmed, people: [], failed: true });
        });
    }, CHATS_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTrimmed, hideTestChats]);
  const searchPending = searchTrimmed.length >= CHATS_SEARCH_MIN_CHARS && searchResult?.text !== searchTrimmed;
  const showSearchResults = searchOpen && searchTrimmed.length >= CHATS_SEARCH_MIN_CHARS;
  // The customer whose chats the list shows instead of the queue (Find a
  // customer, Past chats; Michael 10/23: the same split view, so a chat
  // reads, replies and labels the same as from the queue).
  const [person, setPerson] = useState<CustomerMatch | null>(null);
  const [personList, setPersonList] = useState<{ key: string; items: ChatListItem[]; total: number } | null>(null);
  const [personListFailed, setPersonListFailed] = useState(false);
  // Bumped to reload the person's chats (a label saved, a live update).
  const [personRefresh, setPersonRefresh] = useState(0);
  // A doorbell during a load waits for it, then reloads once (Carlos's #585 review).
  const personLoadRef = useRef({ busy: false, again: false });
  const personKey = person?.key;
  useEffect(() => {
    if (!person) {
      setPersonList(null);
      personLoadRef.current = { busy: false, again: false };
      return undefined;
    }
    let cancelled = false;
    setPersonListFailed(false);
    personLoadRef.current.busy = true;
    const settled = () => {
      if (cancelled) return;
      personLoadRef.current.busy = false;
      if (personLoadRef.current.again) {
        personLoadRef.current.again = false;
        setPersonRefresh((n) => n + 1);
      }
    };
    loadPersonChats(person)
      .then((found) => {
        if (cancelled) return;
        setPersonList({ key: person.key, items: found.items, total: found.total });
        setPersonListFailed(found.failed);
      })
      .catch(() => {
        if (!cancelled) setPersonListFailed(true);
      })
      .finally(settled);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personKey, personRefresh]);
  const personRef = useRef(person);
  personRef.current = person;
  const openPerson = (match: CustomerMatch, item?: ChatListItem) => {
    setSearchOpen(false);
    setPerson(match);
    if (item) openDrawer(item);
  };

  const detailTurnCount = detail?.turns.length ?? 0;
  useEffect(() => {
    // Their message landed: the dots give way to it.
    if (detail && detail.turns[detail.turns.length - 1]?.role === 'customer') clearCustomerTypingFor(detail._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTurnCount]);
  // Their dots show on an AI chat too (Michael, 10/13): support can watch a
  // chat before anyone steps in. Only a resolved chat never shows them.
  const showCustomerTyping =
    !!detail && detail.status !== 'resolved' && !!selectedRow && !!customerTyping[selectedRow.chatId];
  const assistantDots = !!detail && !showCustomerTyping && assistantReplying(detail, now);
  // The open chat's drawer generation, as of this render: a label save
  // started now reports back against it.
  const labelGen = drawerGenRef.current;

  // "Seen" (Michael, 10/13): the customer's `lastSeenAt` as fetched with the
  // chat, moved forward live by chat_seen for the open chat (never back: an
  // older stamp is a replay). A refetch brings the server's own value.
  useLiveChatEvent(true, 'chat_seen', (event) => {
    setDetail((current) => {
      if (!current || current._id !== event.chatId || typeof event.lastSeenAt !== 'string') return current;
      const incoming = new Date(event.lastSeenAt).getTime();
      if (Number.isNaN(incoming)) return current;
      if (current.lastSeenAt && new Date(current.lastSeenAt).getTime() >= incoming) return current;
      return { ...current, lastSeenAt: event.lastSeenAt };
    });
  });
  const seenLabel = detail ? seenLabelFor(detail) : null;

  // Article titles for the open chat's assistant replies (cited and given),
  // one batched lookup for the ids not seen yet — the same lookup the
  // Activity tab uses. Best-effort: an id it can't name shows "Untitled".
  const [docTitles, setDocTitles] = useState<Record<string, string>>({});
  const docTitlesRef = useRef(docTitles);
  docTitlesRef.current = docTitles;
  const sourceIdsKey = detail
    ? Array.from(
        new Set(
          detail.turns
            .filter((t) => t.role === 'agent')
            .flatMap((t) => [...(t.citedDocIds ?? []), ...(t.suppliedDocIds ?? []), ...(t.searchCandidateDocIds ?? [])]),
        ),
      )
        .sort()
        .join(',')
    : '';
  useEffect(() => {
    const missing = sourceIdsKey ? sourceIdsKey.split(',').filter((id) => !(id in docTitlesRef.current)) : [];
    if (missing.length === 0) return;
    let cancelled = false;
    // pom-2-api takes at most KB_TITLES_BATCH ids per call, and a long chat
    // in articles mode can carry more (attack/defend round 3). One failed
    // batch leaves only its own links on the id fallback.
    const batches: string[][] = [];
    for (let i = 0; i < missing.length; i += KB_TITLES_BATCH) batches.push(missing.slice(i, i + KB_TITLES_BATCH));
    batches.forEach((batch) => {
      Promise.resolve()
        .then(() => supportAgentApi.getKbDocTitles(batch))
        .then((found) => {
          if (cancelled || !Array.isArray(found)) return;
          setDocTitles((prev) => ({ ...prev, ...Object.fromEntries(found.map((f) => [f.id, f.title])) }));
        })
        .catch((err) => console.warn('[chats] getKbDocTitles failed — showing article ids', err?.message ?? err));
    });
    return () => {
      cancelled = true;
    };
  }, [sourceIdsKey]);

  // "Seen by support" (Michael, 10/15) — the mirror of the customer's Seen
  // above: POST /chat/:id/staff-seen stamps `staffSeenAt`, and the app shows
  // the customer two green checks on their messages up to it. It means "a
  // person is looking at this chat right now", so it goes out when the open
  // drawer's chat has loaded, when a refetch or live update brings a new
  // customer turn into the open chat, and when the tab comes back to the
  // front with the drawer open — never with the drawer closed, for a
  // resolved chat, or while the tab is hidden, and at most once per chat
  // every CHATS_STAFF_SEEN_MIN_INTERVAL_MS: a trigger inside that window is
  // not dropped but sent once the window ends (one pending send per chat,
  // re-checking the same gates then), so a second customer message landing
  // right after a stamp still turns green without waiting for a third. A
  // reply stamps it server-side, so handleReply never sends it.
  // Fire-and-forget, like the typing note.
  const staffSeenSentAtRef = useRef(new Map<string, number>());
  const staffSeenPendingRef = useRef(new Map<string, number>());
  const staffSeenStatusRef = useRef<ChatStatus | null>(null);
  staffSeenStatusRef.current = detail?.status ?? null;
  const canStampStaffSeen = (chatId: string) =>
    !hiddenRef.current &&
    document.visibilityState === 'visible' &&
    selectedRowRef.current?.chatId === chatId &&
    staffSeenStatusRef.current !== 'resolved';
  const sendStaffSeen = (chatId: string) => {
    const now = Date.now();
    const wait = CHATS_STAFF_SEEN_MIN_INTERVAL_MS - (now - (staffSeenSentAtRef.current.get(chatId) ?? 0));
    if (wait > 0) {
      if (!staffSeenPendingRef.current.has(chatId)) {
        staffSeenPendingRef.current.set(
          chatId,
          window.setTimeout(() => {
            staffSeenPendingRef.current.delete(chatId);
            if (canStampStaffSeen(chatId)) sendStaffSeen(chatId);
          }, wait),
        );
      }
      return;
    }
    staffSeenSentAtRef.current.set(chatId, now);
    Promise.resolve()
      .then(() => supportAgentApi.markChatStaffSeen(chatId))
      .then(() => {
        // The stamp changes no list field, so no doorbell follows: move this
        // row's "Not opened yet" on ourselves.
        const stampedAt = new Date(now).toISOString();
        setActiveChats((prev) => (prev[chatId] ? { ...prev, [chatId]: { ...prev[chatId], staffSeenAt: stampedAt } } : prev));
      })
      .catch((err) => console.warn('[live-chat] markChatStaffSeen failed:', err?.message ?? err));
  };
  useEffect(
    () => () => {
      staffSeenPendingRef.current.forEach((timer) => window.clearTimeout(timer));
      staffSeenPendingRef.current.clear();
    },
    [],
  );
  // What the drawer last showed — tells "this chat just loaded" and "a new
  // customer turn came in" apart from a detail change that is neither (a
  // Seen stamp, our own reply, a status change).
  const staffSeenShownRef = useRef<{ chatId: string; customerTurns: number; status: ChatStatus } | null>(null);
  const detailCustomerTurns = detail ? detail.turns.filter((t) => t.role === 'customer').length : 0;
  useEffect(() => {
    if (!detail) {
      staffSeenShownRef.current = null; // drawer closed, or switching chats
      return;
    }
    const shown = staffSeenShownRef.current;
    staffSeenShownRef.current = { chatId: detail._id, customerTurns: detailCustomerTurns, status: detail.status };
    // Escalating while open (attack/defend A2-3): a person is already looking,
    // so it must not read "Not opened yet".
    const newlyEscalated =
      shown?.chatId === detail._id && shown.status !== 'escalated' && detail.status === 'escalated';
    if (shown?.chatId === detail._id && detailCustomerTurns <= shown.customerTurns && !newlyEscalated) return;
    if (!canStampStaffSeen(detail._id)) return;
    sendStaffSeen(detail._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?._id, detail?.status, detailCustomerTurns]);
  // Back to the tab with the drawer open: they are looking at it again. The
  // listener only exists while a non-resolved chat is open in the drawer.
  useEffect(() => {
    if (!detail || detail.status === 'resolved') return undefined;
    const chatId = detail._id;
    const onVisibilityChange = () => {
      if (canStampStaffSeen(chatId)) sendStaffSeen(chatId);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?._id, detail?.status]);
  // Back to the Chats tab with a chat open: they are looking at it again.
  useEffect(() => {
    if (hidden || !detail || detail.status === 'resolved') return;
    if (canStampStaffSeen(detail._id)) sendStaffSeen(detail._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden]);

  // Keeps the end of the transcript in view for a reader who is already
  // there: on a new turn, when the typing dots come and go, and when the
  // "Seen" caption appears or changes (Michael's dev test, 10/15: the live
  // chat_seen added the caption below the visible bottom, where it stayed
  // until the customer's typing dots scrolled it into view).
  useLayoutEffect(() => {
    const el = transcriptRef.current;
    if (!el || !detail) return;
    const switched = transcriptChatRef.current !== detail._id;
    const statusChanged = transcriptStatusRef.current !== detail.status;
    transcriptChatRef.current = detail._id;
    transcriptStatusRef.current = detail.status;
    // A closed chat scrolls as one piece (below): open it at the end, with
    // the outcome and the label bar in view.
    if (detail.status === 'resolved') {
      const column = paneColumnRef.current;
      if (column && (switched || statusChanged)) column.scrollTop = column.scrollHeight;
      return;
    }
    if (switched || transcriptAtBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      transcriptAtBottomRef.current = true;
    }
    // Opening or closing Details narrows or widens the transcript, so the
    // bubbles rewrap: re-pin a reader who was at the bottom.
  }, [detail?._id, detail?.status, detailTurnCount, showCustomerTyping, assistantDots, seenLabel, detailsChoice ?? wideForDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = window.setInterval(() => {
      load({ quiet: true });
      // A chat that already 404'd stays 404'd — stop hammering it every
      // poll tick (Pollen review, PR #540, low item).
      if (selectedRowRef.current && !chatNotFoundRef.current) {
        void fetchDetail(selectedRowRef.current.chatId, requestSeqRef.current);
      }
    }, liveConnected ? CHATS_POLL_FALLBACK_INTERVAL_MS : CHATS_POLL_INTERVAL_MS);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, viewPages, liveConnected]);

  // "Derek is typing" for the customer (live chat, Michael 10/8). Only an
  // escalated chat takes it (the server answers 409 otherwise), the first
  // keystroke sends it and then one every CHATS_TYPING_SEND_INTERVAL_MS;
  // clearing the box, closing the drawer or leaving the page sends
  // typing:false. A sent reply clears it server-side, so handleReply only
  // forgets it. Fire-and-forget: a failed typing note is not worth a banner.
  const typingRef = useRef<{ chatId: string | null; lastSentAt: number }>({ chatId: null, lastSentAt: 0 });
  const sendTyping = (chatId: string, typing: boolean) => {
    typingRef.current = typing ? { chatId, lastSentAt: Date.now() } : { chatId: null, lastSentAt: 0 };
    Promise.resolve()
      .then(() => supportAgentApi.setChatTyping(chatId, typing))
      .catch((err) => console.warn('[live-chat] setChatTyping failed:', err?.message ?? err));
  };
  const stopTyping = () => {
    const { chatId } = typingRef.current;
    if (chatId) sendTyping(chatId, false);
  };
  const handleReplyInputChange = (value: string) => {
    setReplyInput(value);
    const row = selectedRowRef.current;
    if (!row || detail?.status !== 'escalated') return;
    if (!value.trim()) {
      stopTyping();
      return;
    }
    const { chatId, lastSentAt } = typingRef.current;
    if (chatId !== row.chatId || Date.now() - lastSentAt >= CHATS_TYPING_SEND_INTERVAL_MS) {
      sendTyping(row.chatId, true);
    }
  };
  useEffect(() => () => stopTyping(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // An action's response only applies to the drawer if its chat is still the
  // one open. Resolve can be slow (it waits on the Zendesk archive); if the
  // POM Master closed or switched chats meanwhile, applying it would show
  // chat A's transcript above buttons acting on chat B, and a drawer
  // open/close would discard B's in-flight detail fetch.
  // `startedAtGen` (Michael review, 10/13 — "stuck spinner"; Pollen review,
  // 10/14 — overlapping actions): comparing chatId alone isn't enough.
  // Closing chat A and reopening the SAME chat A while an action is in
  // flight bumps drawerGenRef twice (once on close, once on open) and starts
  // a fresh fetchDetail — so chatId still matches when the action resolves,
  // but applying its outcome would then bump requestSeqRef again,
  // invalidating that fresh fetch before its own `finally` ever runs and
  // leaving `detailLoading` stuck true until the next poll. Comparing
  // drawerGenRef instead of requestSeqRef means a second action completing
  // first on the SAME open chat (e.g. a reply landing while Resolve is still
  // in flight) no longer wrongly reads as "not still selected": only an
  // actual open/close changes drawerGenRef.
  const isStillSelected = (chatId: string, startedAtGen: number) =>
    selectedRowRef.current?.chatId === chatId && drawerGenRef.current === startedAtGen;

  // Chat attachments (Michael, 10/14): files on a staff reply. Offered and
  // accepted only while the chat is escalated; pending files are dropped
  // when the drawer switches chats or the chat leaves that state.
  const uploads = usePendingAttachments({ chatId: selectedRow?.chatId, enabled: detail?.status === 'escalated' });

  // A signed file url lasts 15 minutes; past that, refetch the chat before
  // opening the file. The refetched chat is the server's truth, so it
  // replaces the drawer's detail the same way a poll does.
  const refreshAttachment = async (attachment: ChatAttachment): Promise<ChatAttachment | undefined> => {
    const row = selectedRowRef.current;
    if (!row) return undefined;
    const startedAtGen = drawerGenRef.current;
    const read = startChatRead(row.chatId);
    // A reply, resolve or label landing meanwhile bumps this: then the pane
    // keeps what it has (Fable round, 10/26: the rep's own reply vanished).
    const seq = requestSeqRef.current;
    try {
      const chat = await supportAgentApi.getChat(row.chatId);
      if (!isStillSelected(row.chatId, startedAtGen)) return undefined;
      // Newer state already shown: just find the file's fresh url in this one.
      if (seq === requestSeqRef.current && applyChatRead(row.chatId, read)) {
        setDetail(chat);
        upsertActive(chat, row.chatId);
      }
      return chat.turns.flatMap((turn) => turn.attachments ?? []).find((a) => a.key === attachment.key);
    } catch {
      return undefined;
    }
  };

  const handleReply = async () => {
    const body = replyInput.trim();
    const files = detail?.status === 'escalated' ? uploads.ready : [];
    if (!selectedRow || (!body && files.length === 0) || uploads.blocked || replySubmitting) return;
    setReplyingIn((m) => ({ ...m, [selectedRow.chatId]: true }));
    setReplyError(null);
    // The reply itself clears the typing stamp server-side.
    typingRef.current = { chatId: null, lastSentAt: 0 };
    const actedOn = selectedRow;
    const chatId = actedOn.chatId;
    const startedAtGen = drawerGenRef.current;
    try {
      const chat = await supportAgentApi.replyToChat(
        chatId,
        files.length ? { ...(body ? { body } : {}), attachments: files } : body,
      );
      // Whatever is open now, the list row moves to "Customer's turn".
      upsertActive(chat, chatId);
      markActed(chatId);
      if (!isStillSelected(chatId, startedAtGen)) return;
      // The agent's own reply always scrolls into view, as in the app, even
      // if they had scrolled up to reread before sending.
      transcriptAtBottomRef.current = true;
      setDetail(chat);
      // Invalidates any in-flight background-poll fetchDetail (Interrogator
      // finding, 2026-09-11 round 2 — "poll can clobber a fresh post-action
      // state with a stale pre-action snapshot"): the drawer stays open
      // across this action (no openDrawer/closeDrawer fires), so
      // requestSeqRef never otherwise changes — a poll GET issued before
      // this reply landed would still pass fetchDetail's staleness check
      // and could overwrite the state we just set with what it read before
      // this action completed.
      requestSeqRef.current += 1;
      setReplyInput('');
      uploads.clear();
    } catch (err: any) {
      if (!isStillSelected(chatId, startedAtGen)) {
        // Don't show A's error in B's drawer — but don't drop it either: the
        // typed text was cleared when the drawer switched, so this is the
        // only sign the reply never reached the customer.
        setResolveSnackbar({ message: staleFailureMessage('reply', actedOn), persistent: true });
        return;
      }
      setReplyError(REPLY_ERROR_COPY);
    } finally {
      setReplyingIn((m) => withoutKey(m, chatId));
    }
  };

  // Enter-to-send disables the reply box for the request's duration
  // (`disabled={replySubmitting}`), which drops focus; re-focus it once the
  // send resolves so the agent doesn't have to click back in after every
  // reply (Pollen review, PR #562 — same focus bug as Walt's widget fix,
  // 868mffkep).
  // Only when that same chat's send finished, never another's (Carlos's #585
  // review: focus jumped into B's box mid-typing, so the next keystrokes and
  // Enter went to B's customer).
  const prevReplyRef = useRef<{ chatId?: string; sending: boolean }>({ sending: false });
  useEffect(() => {
    const chatId = selectedRow?.chatId;
    const prev = prevReplyRef.current;
    if (chatId && prev.chatId === chatId && prev.sending && !replySubmitting) {
      replyInputRef.current?.focus();
    }
    prevReplyRef.current = { chatId, sending: replySubmitting };
  }, [replySubmitting, selectedRow]);

  // Distinguishes what actually happened to the archive attempt (Pollen
  // review, finding #7) — collapsing straight to "no Zendesk ticket was
  // created" reads identically whether nothing was ever attempted, the
  // archive is still in flight and can be retried, or a ticket exists but
  // its transcript may be incomplete.
  const resolveSnackbarMessage = (chat: ChatDoc, staleRow?: ChatListItem): string => {
    // A stale outcome (the POM Master switched chats meanwhile) names whose
    // chat it was, so it isn't read as being about the chat now open.
    const prefix = staleRow ? `Chat with ${customerLabel(staleRow)}: ` : '';
    const where = staleRow ? 'that chat (under Closed)' : 'this chat';
    if (chat.zendeskTicketId) {
      return chat.archiveIncomplete
        ? `${prefix}Resolved — Zendesk ticket #${chat.zendeskTicketId} (transcript may be incomplete — verify in Zendesk)`
        : `${prefix}Resolved — Zendesk ticket #${chat.zendeskTicketId}`;
    }
    return chat.archivePending
      ? `${prefix}Resolved — still creating the Zendesk ticket; retry the archive from ${where} if it doesn't appear`
      : `${prefix}Resolved — no Zendesk ticket was created`;
  };

  const staleFailureMessage = (action: 'reply' | 'resolve' | 'archive' | 'label', row: ChatListItem): string => {
    const who = customerLabel(row);
    if (action === 'label') return `Your label for the chat with ${who} was not saved — reopen that chat and try again`;
    if (action === 'reply') return `Your reply to ${who} was not sent — reopen that chat and try again`;
    if (action === 'resolve') return `Couldn't resolve the chat with ${who} — reopen it and try again`;
    return `Couldn't retry the archive for the chat with ${who} — reopen it and try again`;
  };

  // A stale resolve/archive outcome that needs the POM Master's attention
  // (archive pending/incomplete) stays until dismissed; a clean one auto-hides.
  const staleSuccessNotice = (chat: ChatDoc, row: ChatListItem) => ({
    message: resolveSnackbarMessage(chat, row),
    persistent: Boolean(chat.archiveIncomplete || chat.archivePending || !chat.zendeskTicketId),
  });

  const handleResolve = async () => {
    if (!selectedRow || resolveSubmitting) return;
    setResolvingIn((m) => ({ ...m, [selectedRow.chatId]: true }));
    setResolveError(null);
    const actedOn = selectedRow;
    const chatId = actedOn.chatId;
    const startedAtGen = drawerGenRef.current;
    try {
      const chat = await supportAgentApi.resolveChat(chatId);
      // Resolved: it leaves the Active list either way.
      upsertActive(chat, chatId);
      markActed(chatId);
      if (!isStillSelected(chatId, startedAtGen)) {
        void load(); // the list row's status still changed
        setResolveSnackbar(staleSuccessNotice(chat, actedOn));
        return;
      }
      setDetail(chat);
      // See the same-purpose comment in handleReply above.
      requestSeqRef.current += 1;
      setResolveSnackbar({ message: resolveSnackbarMessage(chat) });
      // The Resolve button is gone now; keep keyboard focus in the chat —
      // unless the user has moved it elsewhere while the resolve ran.
      window.requestAnimationFrame(() => {
        const active = document.activeElement;
        if (!active || active === document.body || paneRef.current?.contains(active)) paneHeadingRef.current?.focus();
      });
      void load();
    } catch (err: any) {
      if (!isStillSelected(chatId, startedAtGen)) {
        // Not in B's drawer, but still surfaced (and the list refreshed so
        // A's row shows its real status).
        void load();
        setResolveSnackbar({ message: staleFailureMessage('resolve', actedOn), persistent: true });
        return;
      }
      setResolveError(RESOLVE_ERROR_COPY);
    } finally {
      setResolvingIn((m) => withoutKey(m, chatId));
    }
  };

  // POM-Master-only retry for a chat whose resolve left `archivePending:
  // true` (Pollen review, finding #8 — the backend's retry endpoint had no
  // UI). The server itself is the source of truth for whether a retry is
  // even valid (SUPPORT_AGENT_CHAT_ARCHIVE_NOT_PENDING otherwise); the
  // button below is just gated on `archivePending` to match.
  const handleArchiveRetry = async () => {
    if (!selectedRow || archiveSubmitting) return;
    setArchivingIn((m) => ({ ...m, [selectedRow.chatId]: true }));
    setArchiveError(null);
    const actedOn = selectedRow;
    const chatId = actedOn.chatId;
    const startedAtGen = drawerGenRef.current;
    try {
      const chat = await supportAgentApi.archiveChat(chatId);
      markActed(chatId);
      if (!isStillSelected(chatId, startedAtGen)) {
        void load(); // the list row's status still changed
        setResolveSnackbar(staleSuccessNotice(chat, actedOn));
        return;
      }
      setDetail(chat);
      // See the same-purpose comment in handleReply above.
      requestSeqRef.current += 1;
      setResolveSnackbar({ message: resolveSnackbarMessage(chat) });
      void load();
    } catch (err: any) {
      if (!isStillSelected(chatId, startedAtGen)) {
        void load();
        setResolveSnackbar({ message: staleFailureMessage('archive', actedOn), persistent: true });
        return;
      }
      setArchiveError(ARCHIVE_ERROR_COPY);
    } finally {
      setArchivingIn((m) => withoutKey(m, chatId));
    }
  };

  // The pane header prefers the (fresher) detail, but only if it is this
  // row's chat.
  const currentDetail = detail && selectedRow && detail._id === selectedRow.chatId ? detail : null;

  // Every open chat: in full where fetched, from its list row otherwise —
  // but not while its first full fetch is still on the way.
  const activeList = useMemo(() => {
    const ids = new Set([...Object.keys(activeItems), ...Object.keys(activeChats)]);
    return Array.from(ids)
      .filter((id) => activeChats[id] || !firstFetches[id])
      .map((id) =>
        activeChats[id]
          ? { chat: activeChats[id], listItem: undefined }
          : { chat: docFromListItem(activeItems[id]), listItem: activeItems[id] },
      )
      .sort((a, b) => compareActive(a.chat, b.chat));
  }, [activeChats, activeItems, firstFetches]);
  const supportCount = useMemo(
    () => activeList.filter((entry) => whoseTurn(entry.chat) === 'support').length,
    [activeList],
  );
  const rowModels: ChatRowModel[] = useMemo(
    () =>
      view === 'active'
        ? activeList.map((entry) => activeRowModel(entry.chat, now, entry.listItem))
        : viewList.view === view
          ? viewList.items.map((item) => closedRowModel(item))
          : [],
    [view, activeList, viewList, now],
  );
  const canShowMore = view !== 'active' && viewList.view === view && viewList.items.length < viewList.total;
  // A row reading "12s" ticks every second; minutes and longer, every 30 s.
  // Every second too while "Assistant is typing" shows, so it ends on time.
  const countsSeconds =
    rowModels.some((model) => /\d+s( ago)?$/.test(model.timeText) || model.assistantTyping) || assistantDots;
  useEffect(() => {
    setNow(Date.now());
    const tick = window.setInterval(() => setNow(Date.now()), countsSeconds ? CHATS_CLOCK_FAST_TICK_MS : CHATS_CLOCK_TICK_MS);
    return () => window.clearInterval(tick);
  }, [countsSeconds]);
  // New chat data reads against the time now, not the last tick (attack/
  // defend 10/26: up to 30 s stale, which hid "Assistant is typing" on a
  // message that had just landed).
  useEffect(() => {
    setNow(Date.now());
  }, [activeChats, detail]);
  // The picked customer's chats: what the search found until their own list loads.
  const personRows: ChatRowModel[] = person
    ? (personList?.key === person.key ? personList.items : person.chats).map((item) => personRowModel(item, person))
    : [];
  const emptyCopy =
    view === 'active' ? 'No open chats right now.' : view === 'label' ? 'Nothing to label right now.' : 'No closed chats yet.';
  const currentTurn = currentDetail ? whoseTurn(currentDetail) : null;
  // The details pane: open by default from 1400px (the design's width),
  // closed below it — the Details button opens or closes it at any width.
  const showDetails = detailsChoice ?? wideForDetails;
  const dividerAt = detail ? escalationIndex(detail) : -1;
  // Only GET /chat/:id carries the organization name; a reply, resolve or
  // label response doesn't, so the details pane keeps the last one it saw.
  const orgNamesRef = useRef(new Map<string, string>());
  if (detail?.orgName) orgNamesRef.current.set(detail._id, detail.orgName);
  // Another rep typing in the open chat.
  const otherRepTyping = selectedRow && detail?.status === 'escalated' ? staffTyping[selectedRow.chatId] : undefined;

  // A saved label: the open chat shows it, and the list moves the chat out
  // of Needs labeling.
  const handleLabelSaved = (chat: ChatDoc, gen: number) => {
    markActed(chat._id);
    setDetail((current) => (current && current._id === chat._id ? { ...chat, lastSeenAt: laterLastSeenAt(current.lastSeenAt, chat.lastSeenAt) } : current));
    // See the same-purpose comment in handleReply — but only while the saved
    // chat is still the open one: a save landing after the rep opened another
    // chat would discard that chat's own load and strand its spinner
    // (attack/defend 10/26).
    // By drawer generation, not chat id: closing and reopening the same chat
    // mid-save is a new load too (attack/defend round 2, 10/26).
    if (isStillSelected(chat._id, gen)) requestSeqRef.current += 1;
    // The other list views' remembered rows no longer hold for this chat.
    (['label', 'closed'] as const).forEach((v) => {
      if (v !== viewRef.current) delete listCacheRef.current[v];
    });
    void load({ quiet: true });
    if (person) setPersonRefresh((n) => n + 1);
  };
  // A label that failed after the rep moved on: its panel is gone, so say so here.
  const handleLabelFailed = (chat: ChatDoc, gen: number) => {
    if (isStillSelected(chat._id, gen)) return;
    setResolveSnackbar({ message: staleFailureMessage('label', listItemFromDoc(chat)), persistent: true });
  };

  const viewToggle = (
    <ToggleButtonGroup
      size="small"
      value={view === 'label' ? 'closed' : view}
      exclusive
      fullWidth
      aria-label="Show chats"
      onChange={(_, next) => {
        if (next) switchView(next);
      }}
      sx={{
        mt: 1,
        bgcolor: 'grey.200',
        borderRadius: 2,
        p: 0.375,
        gap: 0.5,
        '& .MuiToggleButton-root': {
          border: 0,
          borderRadius: '6px !important',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600,
          color: 'grey.700',
          px: 1,
          py: 0.5,
          minHeight: 30,
          whiteSpace: 'nowrap',
          '&.Mui-selected, &.Mui-selected:hover': {
            bgcolor: 'common.white',
            color: 'grey.800',
            boxShadow: '0 1px 3px rgba(22, 28, 36, 0.12)',
          },
        },
      }}
    >
      <ToggleButton value="active">
        Active
        {supportCount > 0 && (
          <Box
            component="span"
            data-testid="chats-support-count"
            aria-label={`${supportCount} waiting on support`}
            sx={{
              ml: 1,
              minWidth: 20,
              height: 20,
              px: 0.75,
              borderRadius: 10,
              bgcolor: 'warning.main',
              color: 'grey.800',
              fontSize: 12,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {supportCount}
          </Box>
        )}
      </ToggleButton>
      <ToggleButton value="closed">Closed</ToggleButton>
    </ToggleButtonGroup>
  );

  // Closed's Needs label filter (Michael, 10/23: a filter, not its own tab):
  // closed chats that were escalated or rated "Not at all" and have no label
  // yet — the rows with the dashed tag.
  const labelOnly = view === 'label';
  const labelFilter =
    view === 'active' ? null : (
      <ButtonBase
        aria-pressed={labelOnly}
        data-testid="chats-label-filter"
        onClick={() => switchView(labelOnly ? 'closed' : 'label')}
        sx={{
          mt: 1,
          px: 1.25,
          minHeight: 28,
          gap: 0.5,
          border: 1,
          borderColor: labelOnly ? '#4B2FA8' : 'grey.400',
          borderRadius: 999,
          bgcolor: labelOnly ? '#EFE9FF' : 'background.paper',
          color: labelOnly ? '#4B2FA8' : 'grey.800',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 600,
          '&:hover': { borderColor: '#4B2FA8' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.dark', outlineOffset: 2 },
        }}
      >
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: '#4B2FA8' }}>
          <MissingLabelIcon />
        </Box>
        Needs label
        {labelTotal ? (
          <Box component="span" data-testid="chats-label-count" sx={{ fontWeight: 600 }}>
            · {labelTotal}
          </Box>
        ) : null}
      </ButtonBase>
    );

  // Every list reloads without them, or with them back; the cached rows of
  // other views were loaded the other way, so they go.
  const toggleHideTestChats = (on: boolean) => {
    hideTestChatsRef.current = on;
    setHideTestChats(on);
    writeHideTestChats(on);
    listCacheRef.current = {};
    void load();
  };
  const hideTestChatsSwitch =
    showTestChatsSwitch ? (
      <FormControlLabel
        data-testid="chats-hide-test"
        title="Leaves out chats made on dev or QA"
        control={<Switch size="small" checked={hideTestChats} onChange={(e) => toggleHideTestChats(e.target.checked)} />}
        label={<Typography variant="caption" sx={{ fontWeight: 600 }}>Hide test chats</Typography>}
        sx={{ display: 'flex', mt: 0.75, ml: 0, mr: 0, gap: 0.5 }}
      />
    ) : null;

  // Live / Connecting / Reconnecting, at the foot of the list's header
  // (Michael, 10/23: its own row above the inbox cost the chat its height).
  const liveStatusBadge = liveStatus ? (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          data-testid="chats-live-status"
          title={liveConnected ? 'Live updates are on' : `Checking for changes every ${CHATS_POLL_INTERVAL_MS / 1000} s`}
          sx={{
            flex: '0 0 auto',
            whiteSpace: 'nowrap',
            ...(!liveConnected && { bgcolor: '#FFF7CD', color: '#7A4F01', borderRadius: 999, px: 1, py: 0.25 }),
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: liveConnected ? 'success.main' : 'warning.dark',
              ...(liveConnected && {
                animation: 'chats-live-pulse 2s infinite',
                '@keyframes chats-live-pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(37,182,96,.5)' },
                  '70%': { boxShadow: '0 0 0 8px rgba(37,182,96,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(37,182,96,0)' },
                },
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }),
            }}
          />
          <Typography variant="caption" sx={{ fontWeight: 600, color: liveConnected ? 'success.darker' : 'inherit' }}>
            {liveConnected ? 'Live' : `${liveStatus === 'reconnecting' ? 'Reconnecting' : 'Connecting'}…`}
          </Typography>
        </Stack>
  ) : null;

  const searchBox = (
    <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 1,
            borderColor: 'grey.400',
            borderRadius: 2,
            px: 1.25,
            height: 36,
            color: 'text.secondary',
            '&:focus-within': { borderColor: 'primary.dark', boxShadow: '0 0 0 3px rgba(32,111,165,.2)' },
          }}
        >
          <SearchIcon fontSize="small" />
          <InputBase
            type="search"
            placeholder="Find a customer"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setSearchOpen(false);
            }}
            inputProps={{
              'aria-label': 'Find a customer',
              'aria-expanded': showSearchResults,
              'aria-controls': 'chats-search-results',
              autoComplete: 'off',
              // pom-2-api #604's `search` takes 2–100 characters; past that it
              // answered an error, not "no one matches" (Carlos's #585 review).
              maxLength: CHATS_SEARCH_MAX_CHARS,
            }}
            sx={{ flex: '1 1 auto', minWidth: 0, fontSize: 14, color: 'text.primary' }}
          />
        </Box>
        {showSearchResults && (
          <Paper
            id="chats-search-results"
            data-testid="chats-search-results"
            elevation={8}
            sx={{ position: 'absolute', top: 42, left: 0, right: 0, zIndex: 5, borderRadius: 3, overflow: 'hidden' }}
          >
            <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {searchPending && (
                <Box sx={{ p: 1.5 }}>
                  <CircularProgress size={16} aria-label="Searching" />
                </Box>
              )}
              {!searchPending &&
                searchResult?.people.map((match) => (
                  <ButtonBase
                    key={match.key}
                    data-testid="chats-search-result"
                    onClick={() => openPerson(match)}
                    sx={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: '36px minmax(0, 1fr) auto',
                      columnGap: 1.5,
                      alignItems: 'center',
                      textAlign: 'left',
                      px: 1.75,
                      py: 1.25,
                      borderTop: 1,
                      borderColor: 'divider',
                      fontFamily: 'inherit',
                      '&:hover': { bgcolor: '#F9FAFB' },
                      '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.dark', outlineOffset: -2 },
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: '#F4F6F8',
                        color: '#454F5B',
                        fontSize: 13,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {initialsFor(match.label)}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography component="span" variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {match.label}
                        </Typography>
                        {loginChannels(match).map((channel) => (
                          <ChannelIcon key={channel} channel={channel} />
                        ))}
                      </Stack>
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {match.contact}
                      </Typography>
                    </Box>
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                      {match.chats.length === 1 ? '1 chat' : `${match.chats.length} chats`}
                    </Typography>
                  </ButtonBase>
                ))}
              {!searchPending && searchResult && searchResult.people.length === 0 && (
                <Typography variant="body2" sx={{ p: 1.75, color: 'grey.700' }}>
                  {searchResult.failed ? "Couldn't search — try again" : `No one who has chatted matches “${searchResult.text}”.`}
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 1.75, py: 1.25, borderTop: 1, borderColor: 'divider', bgcolor: '#F9FAFB' }}>
              <Typography variant="caption" color="text.secondary">
                {!searchPending && searchResult?.capped
                  ? `From the newest ${CHATS_SEARCH_LIMIT} matching chats — add more to the search to narrow it.`
                  : 'Searches people who have chatted.'}
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );

  return (
    <Box ref={panelRef} sx={{ mb: `${CHATS_SPLIT_BOTTOM_GAP - pagePaddingBelow}px` }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Split view (Michael, 10/22): the list on the left, the selected chat
          on the right — Derek switches chats with one click and keeps the
          queue in view while he types. Below md it is one or the other. Flush
          in the page and down to the bottom of the screen (Michael, 10/23: no
          window within a window); the AI-chat button's corner is kept clear
          by the columns themselves (CHAT_BUTTON_CLEARANCE). */}
      <Box
        ref={splitRef}
        data-testid="chats-split"
        sx={{
          display: 'flex',
          borderTop: 1,
          borderColor: 'divider',
          overflow: 'hidden',
          height: splitHeight ?? `calc(100vh - 320px)`,
          minHeight: CHATS_SPLIT_MIN_HEIGHT,
          '& [data-scroll]': { overscrollBehavior: 'contain' },
        }}
      >
        <Box
          component="nav"
          ref={listNavRef}
          tabIndex={-1}
          aria-label="Chats"
          sx={{
            // A little narrower while the details pane is open on a smaller window.
            width: { xs: '100%', md: selectedRow && showDetails && !wideForDetails ? 300 : 360 },
            flexShrink: 0,
            borderRight: { md: 1 },
            borderColor: 'divider',
            flexDirection: 'column',
            display: { xs: selectedRow ? 'none' : 'flex', md: 'flex' },
          }}
        >
          {/* Search, then Active / Closed and Closed's Needs label filter — or whose
              chats these are. The rows scroll under them. */}
          <Box sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider', position: 'relative', zIndex: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{searchBox}</Box>
              {liveStatusBadge}
            </Stack>
            {person ? (
              <CustomerSummary
                person={person}
                total={personList?.key === person.key ? personList.total : null}
                shown={personRows.length}
                onBack={() => setPerson(null)}
              />
            ) : (
              <>
                {viewToggle}
                {labelFilter}
                {hideTestChatsSwitch}
              </>
            )}
          </Box>
          <Box data-scroll sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', pb: { xs: CHAT_BUTTON_CLEARANCE, md: 0 } }}>
          {person ? (
            <>
              {personListFailed && (
                <Alert severity="warning" sx={{ m: 1.5 }}>
                  Couldn't load all of this person's chats — showing what the search found.
                </Alert>
              )}
              {personRows.map((model) => (
                <ChatRow
                  key={model.chatId}
                  model={model}
                  selected={selectedRow?.chatId === model.chatId}
                  typing={false}
                  onSelect={() => openDrawer(model.row)}
                />
              ))}
            </>
          ) : (
            <>
          {loading && rowModels.length === 0 && (
            <Box sx={{ p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loading && rowModels.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {emptyCopy}
            </Typography>
          )}
          {rowModels.map((model) => (
            <ChatRow
              key={model.chatId}
              model={model}
              selected={selectedRow?.chatId === model.chatId}
              typing={view === 'active' && !!customerTyping[model.chatId]}
              staffTyping={view === 'active' ? staffTyping[model.chatId] : undefined}
              onSelect={() => openDrawer(model.row)}
            />
          ))}
          {canShowMore && (
            <Box sx={{ p: 1.5 }}>
              <Button size="small" onClick={() => setViewPages((n) => n + 1)}>
                Show more
              </Button>
            </Box>
          )}
          {view === 'active' && activeOverflow > 0 && (
            <Typography variant="caption" color="text.secondary" data-testid="chats-active-overflow" sx={{ display: 'block', p: 1.5 }}>
              {activeOverflow} more open chat{activeOverflow === 1 ? '' : 's'} not shown: the list holds the{' '}
              {CHATS_ACTIVE_LIMIT * CHATS_ACTIVE_MAX_PAGES} most recently active of each kind.
            </Typography>
          )}
            </>
          )}
          </Box>
        </Box>

        <Box
          component="section"
          ref={paneRef}
          aria-label="Selected chat"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            display: { xs: selectedRow ? 'flex' : 'none', md: 'flex' },
            flexDirection: 'column',
          }}
        >
          {!selectedRow ? (
            <Box sx={{ m: 'auto', p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Pick a chat from the list.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ px: 2.5, pt: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={1.5}
                  useFlexGap
                  sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 1 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flexWrap: 'wrap', rowGap: 0.5 }}>
                    {currentDetail && currentTurn === 'support' && isUrgent(currentDetail) && (
                      <Chip size="small" label="Urgent" sx={{ ...pillSx, height: 22, fontSize: 12, ...URGENT_SX }} />
                    )}
                    <Typography
                      variant="h6"
                      ref={paneHeadingRef}
                      tabIndex={-1}
                      sx={{ fontSize: 18, fontWeight: 600, overflowWrap: 'anywhere', '&:focus': { outline: 'none' } }}
                    >
                      {customerLabel(currentDetail ?? selectedRow)}
                    </Typography>
                    <HandlerIcon
                      by={handledBy(currentDetail ?? selectedRow)}
                      size={18}
                      testId="chat-handler"
                    />
                    <AppChip channel={currentDetail?.channel ?? selectedRow.channel} />
                    {!chatNotFound && (currentTurn === null || currentTurn === 'closed') && (
                      <Box sx={{ alignSelf: 'center' }}>
                        <StatusChip status={detail?.status ?? selectedRow.status} />
                      </Box>
                    )}
                    {currentTurn && currentTurn !== 'closed' && (
                      <Chip
                        size="small"
                        data-testid="chat-whose-turn"
                        label={WHOSE_TURN_LABEL[currentTurn]}
                        sx={{ ...pillSx, height: 22, fontSize: 12, ...WHOSE_TURN_SX[currentTurn] }}
                      />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {currentDetail?.status === 'escalated' && !detailLoading && (
                      <Button
                        variant="outlined"
                        color="success"
                        disabled={resolveSubmitting}
                        onClick={handleResolve}
                        // The design's darker green text: success.main on white is under AA.
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          height: 36,
                          color: 'success.darker',
                          borderColor: 'success.main',
                          '&:hover': { borderColor: 'success.dark', bgcolor: 'success.lighter' },
                        }}
                      >
                        {resolveSubmitting ? 'Resolving…' : 'Resolve'}
                      </Button>
                    )}
                    {currentDetail && !detailLoading && (
                      <Button
                        variant="text"
                        aria-expanded={showDetails}
                        aria-controls={showDetails ? 'chat-details-pane' : undefined}
                        onClick={() => setDetailsChoice(!showDetails)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          height: 36,
                          color: showDetails ? 'primary.dark' : 'grey.700',
                          bgcolor: showDetails ? '#E6F6FC' : 'transparent',
                        }}
                      >
                        Details
                      </Button>
                    )}
                    <IconButton onClick={closeDrawer} aria-label="Close">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
                {selectedRow && !chatNotFound && (() => {
                  const turnCount = detail?.turns.length ?? selectedRow.turnCount;
                  return (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      data-testid="chat-caption"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Started {new Date(detail?.createdAt ?? selectedRow.createdAt).toLocaleString()} ·{' '}
                      {turnCount} turn{turnCount === 1 ? '' : 's'}
                      {detail?.escalation?.at && ` · Escalated ${new Date(detail.escalation.at).toLocaleString()}`}
                      {detail?.zendeskTicketId && (
                        <>
                          {' · '}
                          <MuiLink href={zendeskTicketUrl(detail.zendeskTicketId)} target="_blank" rel="noopener noreferrer">
                            Zendesk #{detail.zendeskTicketId}
                          </MuiLink>
                        </>
                      )}
                    </Typography>
                  );
                })()}
              </Box>
              <Divider />
              {/* On a phone the details stack under the conversation instead of
                  squeezing it (attack/defend round 3); still never over it. */}
              <Box sx={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: { xs: 'column', sm: 'row' } }}>
                {/* overflowY (attack/defend A3-1): a long feedback comment, alerts or
                    queued files scroll with the column instead of squeezing the
                    transcript to nothing and cutting off Retry archive / Resolve. */}
                <Box
                  ref={paneColumnRef}
                  data-scroll
                  data-testid="chat-pane-column"
                  sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', p: 2, overflowY: 'auto' }}
                >
                  {detailLoading && <CircularProgress size={20} />}
                  {chatNotFound && <Alert severity="warning">Chat not found.</Alert>}
                  {detailError && !chatNotFound && <Alert severity="warning">{detailError}</Alert>}

                  {detail && !detailLoading && (
                    <>
                      {/* flex: '1 1 auto' + minHeight: 0 — fills the pane and
                          scrolls, so the reply box stays at the bottom of it. */}
                      <Box
                        ref={transcriptRef}
                        data-scroll
                        data-testid="chat-transcript"
                        onScroll={(e) => {
                          const el = e.currentTarget;
                          transcriptAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
                        }}
                        // One scroll at a time (Michael, 10/23: "why do I have to
                        // scroll down just to get to something where I have to
                        // scroll down again"): an open chat scrolls its messages
                        // above the pinned reply box; a closed one scrolls as one
                        // piece in the column, messages, outcome and label together.
                        sx={
                          detail.status === 'resolved'
                            ? { flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }
                            : {
                                flex: '1 1 auto',
                                minHeight: 160,
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                mb: 2,
                              }
                        }
                      >
                        {detail.turns.map((turn, i) => (
                          <React.Fragment key={i}>
                            {i === dividerAt && <EscalationDivider chat={detail} />}
                            <TurnLine
                              turn={turn}
                              showDate={showDateFor(detail.turns, i)}
                              onRefreshAttachment={refreshAttachment}
                              sources={
                                turn.role === 'agent' ? (
                                  <ReplySources turn={turn} titles={docTitles} before={previousGiven(detail.turns, i)} />
                                ) : undefined
                              }
                            />
                          </React.Fragment>
                        ))}
                        {dividerAt === detail.turns.length && <EscalationDivider chat={detail} />}
                        {seenLabel && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            data-testid="chat-seen"
                            sx={{ alignSelf: 'flex-end', mt: -1, pr: 0.5 }}
                          >
                            {seenLabel}
                          </Typography>
                        )}
                        {showCustomerTyping && <CustomerTypingDots />}
                        {assistantDots && <AssistantTypingDots />}
                      </Box>

                      {/* Rightmost column (no details beside it): its Send / Save /
                          Retry stay out from under the AI-chat button. */}
                      <Box data-testid="chat-pane-actions" sx={{ pr: showDetails ? 0 : CHAT_BUTTON_CLEARANCE }}>
                      {resolveError && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                          {resolveError}
                        </Alert>
                      )}

                      {detail.status === 'escalated' && (
                        <>
                          {replyError && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              {replyError}
                            </Alert>
                          )}
                          {uploads.error && (
                            <Alert severity="warning" sx={{ mb: 1 }} data-testid="chat-attachment-error">
                              {uploads.error}
                            </Alert>
                          )}
                          {otherRepTyping && (
                            <Typography
                              role="status"
                              variant="caption"
                              data-testid="chat-other-rep-typing"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'grey.700', fontWeight: 600 }}
                            >
                              <TypingDots size={5} />
                              {otherRepTyping} is typing in this chat too
                            </Typography>
                          )}
                          <PendingAttachmentList items={uploads.items} onRemove={uploads.remove} onRetry={uploads.retry} disabled={replySubmitting} />
                          <Typography
                            component="label"
                            htmlFor="chat-reply-input"
                            variant="body2"
                            sx={{ display: 'block', fontWeight: 600, color: 'grey.700', mb: 0.75, overflowWrap: 'anywhere' }}
                          >
                            Reply to {customerLabel(detail)}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                            <AttachmentPickerButton disabled={replySubmitting} onFiles={uploads.add} />
                            <TextField
                              fullWidth
                              multiline
                              maxRows={4}
                              size="small"
                              id="chat-reply-input"
                              placeholder="Reply to this chat…"
                              helperText="Enter to send, Shift+Enter for a new line"
                              value={replyInput}
                              onChange={(e) => handleReplyInputChange(e.target.value)}
                              onKeyDown={(e) => {
                                // Enter sends, like any chat; Shift+Enter is a new line.
                                // Not mid-composition (IME), where Enter picks a candidate.
                                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                                  e.preventDefault();
                                  void handleReply();
                                }
                              }}
                              disabled={replySubmitting}
                              inputRef={replyInputRef}
                            />
                            <Button
                              variant="contained"
                              disabled={(!replyInput.trim() && uploads.ready.length === 0) || uploads.blocked || replySubmitting}
                              onClick={handleReply}
                              sx={{ height: 40, px: 2.5, textTransform: 'none', fontWeight: 600, bgcolor: 'primary.dark' }}
                            >
                              {replySubmitting ? 'Sending…' : 'Send'}
                            </Button>
                          </Stack>
                        </>
                      )}
                      {detail.status === 'ai' && (
                        <Typography variant="body2" color="text.secondary">
                          The assistant is handling this chat. You can reply once it is escalated.
                        </Typography>
                      )}
                      {detail.status === 'resolved' && (
                        <>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Resolved{detail.resolvedAt ? ` ${new Date(detail.resolvedAt).toLocaleString()}` : ''}
                            {detail.resolvedBy ? ` — ${resolvedByLabel(detail, myEmail)}` : ''}
                            {detail.zendeskTicketId
                              ? detail.archiveIncomplete
                                ? ' — ticket created, transcript may be incomplete.'
                                : ' — Zendesk ticket created.'
                              : detail.archivePending
                                ? ' — Zendesk ticket not created yet.'
                                : ' — no Zendesk ticket was created.'}
                          </Typography>
                          {detail.feedback && (
                            <Box data-testid="chat-feedback-summary">
                              <FeedbackSummary feedback={detail.feedback} />
                              {/* The comment is its own block (Michael, 10/20): it can run to
                                  1000 chars with line breaks the widget keeps (pre-wrap), and
                                  in the "Needs review" queue it is the payload. */}
                              {detail.feedback.comment && (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }} data-testid="chat-feedback-comment">
                                  “{detail.feedback.comment}”
                                </Typography>
                              )}
                            </Box>
                          )}
                          {/* The label, where the reply box sits on an open chat (Michael, 10/23). */}
                          {reviewKindFor(detail) && (
                            <ChatLabelPanel
                              key={detail._id}
                              chat={detail}
                              titles={docTitles}
                              myEmail={myEmail}
                              inNeedsLabeling={view === 'label' && !person}
                              onSaved={(c) => handleLabelSaved(c, labelGen)}
                              onFailed={(c) => handleLabelFailed(c, labelGen)}
                            />
                          )}
                          {archiveError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {archiveError}
                            </Alert>
                          )}
                          {detail.archivePending && (
                            <Button
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={{ mt: 1 }}
                              disabled={archiveSubmitting}
                              onClick={handleArchiveRetry}
                            >
                              {archiveSubmitting ? 'Retrying…' : 'Retry archive to Zendesk'}
                            </Button>
                          )}
                        </>
                      )}
                      </Box>
                    </>
                  )}
                </Box>
                {currentDetail && !detailLoading && showDetails && (
                  <Box
                    component="aside"
                    id="chat-details-pane"
                    aria-label="Chat details"
                    // Beside the conversation, never over it (Michael, 10/22): opening
                    // it on a narrower window narrows the chat instead.
                    sx={{
                      width: { xs: '100%', sm: 220, lg: 240 },
                      maxHeight: { xs: '40%', sm: 'none' },
                      flexShrink: 0,
                      borderLeft: { xs: 0, sm: 1 },
                      borderTop: { xs: 1, sm: 0 },
                      borderColor: 'divider',
                      bgcolor: '#F9FAFB',
                      p: 2,
                      // The rightmost column: its last lines scroll up past the AI-chat button.
                      pb: '88px',
                      overflowY: 'auto',
                      '@media (min-width: 1400px)': { width: 260, p: 2.5, pb: '88px' },
                    }}
                  >
                    <ChatDetails
                      chat={orgNamesRef.current.has(currentDetail._id) && !currentDetail.orgName
                        ? { ...currentDetail, orgName: orgNamesRef.current.get(currentDetail._id) }
                        : currentDetail}
                      myEmail={myEmail}
                    />
                    <PastChats chat={currentDetail} onPick={openPerson} />
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={resolveSnackbar !== null}
        autoHideDuration={resolveSnackbar?.persistent ? null : 5000}
        onClose={(_e, reason) => {
          // A persistent notice ignores click-away; only its close button dismisses it.
          if (resolveSnackbar?.persistent && reason === 'clickaway') return;
          setResolveSnackbar(null);
        }}
        message={resolveSnackbar?.message}
        action={
          resolveSnackbar?.persistent ? (
            <IconButton size="small" color="inherit" aria-label="Dismiss notice" onClick={() => setResolveSnackbar(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : undefined
        }
      />
    </Box>
  );
};

export default ChatsPanel;
